//client/src/pages/student/SentencePractice.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, saveLessonModeProgress } from "@/api/apiClient";
import { useLocation, useNavigate } from "react-router-dom";

import { uiFor } from "@/lib/modeUi";

import { track } from "@/lib/track";
import { getStoredFunnelContext, sendToFunnelSheet } from "@/lib/funnelSheet";

import PracticeHeader from "@/components/student/PracticeHeader";
import PromptCard from "@/components/student/PromptCard";

import { readProgress, writeProgress, pct } from "@/lib/progressStore";

import { useAuth } from "../../context/AuthContext";
import ReorderExerciseCard from "@/components/practice/ReorderExerciseCard";

// ===== helpers (reorder/typing normalization) =====
const norm = (v) =>
  String(v ?? "")
    .trim()
    .replace(/\s+/g, " ");

const toTextArray = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((x) =>
      typeof x === "string"
        ? x
        : (x?.text ?? x?.word ?? x?.label ?? x?.value ?? ""),
    )
    .map(norm)
    .filter(Boolean);

const arraysEqualStrict = (a, b) => {
  const aa = toTextArray(a);
  const bb = toTextArray(b);
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false;
  return true;
};

// Create stable tokens so duplicates are safe (e.g., "to", "to")
const makeTokens = (words, qid = "q") =>
  toTextArray(words).map((text, idx) => ({ id: `${qid}-${idx}`, text }));

function asArr(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const t = value.trim();
    return t ? t.split(/\s+/) : [];
  }
  return [];
}

function normalizeText(s) {
  return String(s || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const DEFAULT_PRACTICE_MODE = "reorder";
const SUPPORTED_PRACTICE_MODES = new Set([
  "reorder",
  "typing",
  "cloze",
  "audio",
]);
const MAX_ATTEMPTS = 3;
const QUICK_START_DAY_NUMBER = 900001;

export default function SentencePractice() {
  const { mode: urlMode } = useParams();

  // robust fallback: extract mode from URL path if useParams fails
  const segments = window.location.pathname.split("/").filter(Boolean);
  const practiceIdx = segments.indexOf("practice");
  const pathMode = practiceIdx >= 0 ? segments[practiceIdx + 1] : null;

  const { auth } = useAuth();

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const hasPaidAccess =
    auth?.user?.hasAccess === true ||
    auth?.user?.has_access === true ||
    auth?.hasAccess === true ||
    auth?.has_access === true ||
    storedUser?.hasAccess === true ||
    storedUser?.has_access === true;

  const progressUserId =
    auth?.user?.id ||
    auth?.user?.email ||
    auth?.id ||
    auth?.email ||
    storedUser?.id ||
    storedUser?.email ||
    null;

  const rawMode = String(
    urlMode || pathMode || DEFAULT_PRACTICE_MODE,
  ).toLowerCase();

  const activeMode = SUPPORTED_PRACTICE_MODES.has(rawMode)
    ? rawMode
    : DEFAULT_PRACTICE_MODE;

  // UI mode (what the user sees)
  const safeMode = SUPPORTED_PRACTICE_MODES.has(activeMode)
    ? activeMode
    : DEFAULT_PRACTICE_MODE;

  const NEXT_MODE = {
    reorder: "typing",
    typing: "audio",
    audio: "reorder",
    cloze: "reorder",
  };

  const fallbackMode = NEXT_MODE[safeMode] || "reorder";

  // API fetch mode: supports reorder/typing/audio. Cloze is experimental → fall back to reorder.
  const fetchMode = safeMode === "cloze" ? "reorder" : safeMode;

  // XP mode (what we send to backend XP pipeline)
  // Backend currently supports only typing/reorder
  const xpMode = safeMode === "reorder" ? "reorder" : "typing";

  const search = new URLSearchParams(window.location.search);
  const lessonId = Number(search.get("lessonId") || 1);

  const navigate = useNavigate();

  const lessonIdNumSafe = lessonId || 1;
  const nextLessonId = lessonIdNumSafe + 1;

  const difficulty = String(
    search.get("difficulty") || "beginner",
  ).toLowerCase();

  const onboardingSource = search.get("source") || "";

  const isLesson1ChallengeOnboarding =
    lessonId === 1 &&
    difficulty === "beginner" &&
    safeMode === "reorder" &&
    search.get("onboarding") === "1" &&
    ["spoken-english-challenge", "spoken-english-vsl-trial"].includes(
      onboardingSource,
    );

  const showLesson1ConversionComplete =
    isLesson1ChallengeOnboarding && !hasPaidAccess;

  const isStrictQuickStart =
    search.get("context") === "quick-start" &&
    lessonId === QUICK_START_DAY_NUMBER &&
    difficulty === "beginner";

  const quickStartSegment = String(
    search.get("segment") ||
      storedUser?.segment ||
      localStorage.getItem("fj_segment") ||
      localStorage.getItem("fj_level_segment") ||
      "general",
  )
    .trim()
    .toLowerCase();

  function addQuickStartParams(params) {
    if (!isStrictQuickStart) return params;
    params.set("lessonId", String(QUICK_START_DAY_NUMBER));
    params.set("difficulty", "beginner");
    params.set("context", "quick-start");
    params.set("source", "free_live_class");
    params.set("segment", quickStartSegment);
    return params;
  }

  function sendQuickStartAction(action, mode = safeMode, variant = "") {
    if (!isStrictQuickStart) return;

    const context = getStoredFunnelContext();
    const contextUser = context.user || storedUser || {};

    sendToFunnelSheet({
      type: "quick_start_action",
      action,
      source: "free_live_class",
      name: contextUser.name || "",
      email: contextUser.email || "",
      whatsapp_number:
        contextUser.whatsapp_number || contextUser.whatsapp || "",
      segment: quickStartSegment,
      main_goal: context.main_goal || contextUser.main_goal || "",
      track: context.track || contextUser.track || "BEGINNER",
      mode,
      variant,
      page_url: window.location.href,
    });
  }

  function goLessons() {
    navigate("/lessons", { replace: true });
  }

  async function continueToFreeLessons() {
    try {
      const milestoneRequest = api.api.post("/funnel/journey-milestone", {
        milestoneType: "LEARNING_PATH_EXPLORED",
      });
      const result = await Promise.race([
        milestoneRequest,
        new Promise((resolve) =>
          setTimeout(() => resolve({ ok: false, timedOut: true }), 1500),
        ),
      ]);

      if (!result?.ok) {
        console.warn(
          result?.timedOut
            ? "[journey] Learning path milestone request timed out."
            : "[journey] Learning path milestone was not recorded.",
        );
      }
    } catch {
      console.warn("[journey] Learning path milestone request failed.");
    } finally {
      navigate("/b/lessons");
    }
  }

  function goNextLesson() {
    if (!nextLessonId) return goLessons();
    navigate(
      `/lesson/${nextLessonId}?difficulty=${encodeURIComponent(difficulty)}&autostart=1`,
      { replace: true },
    );
  }

  const asArr = (v) => (Array.isArray(v) ? v : []);
  const norm = (s) =>
    String(s || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();

  function goAudio(variant) {
    if (!lessonIdNumSafe) return;
    const params = addQuickStartParams(
      new URLSearchParams({
        lessonId: String(lessonIdNumSafe),
        difficulty,
        variant,
      }),
    );
    navigate(`/practice/audio?${params.toString()}`);
  }

  function persistModeProgress(modeOverride, completedCount) {
    try {
      const stableId = Number(lid || lessonId || 0);
      if (!stableId) return;

      const modeKey =
        modeOverride || (safeMode === "audio" ? "audio" : safeMode);

      if (!progressUserId) return;

      const prev = readProgress(progressUserId, stableId, modeKey) || {};
      const totalNow = Number(
        prev?.total || lessonExercises?.length || totalQuestions || 0,
      );

      if (!totalNow || totalNow <= 0) return;

      const nextCompleted = Math.max(
        Number(prev?.completed || 0),
        Number(completedCount || 0),
      );

      writeProgress(progressUserId, stableId, modeKey, {
        completed: nextCompleted,
        total: totalNow,
        updatedAt: Date.now(),
      });

      persistBackendLessonModeProgress({
        lessonId: stableId,
        mode: modeKey,
        completed: nextCompleted,
        total: totalNow,
      });
    } catch {
      // ignore
    }
  }

  function stopTTS() {
    try {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } catch {}
    setIsSpeaking(false);
  }

  function speakTTS(text) {
    try {
      if (!("speechSynthesis" in window)) return;
      if (!text) return;

      window.speechSynthesis.cancel();

      const u = new SpeechSynthesisUtterance(String(text));

      // ✅ always latest values (no stale closure)
      u.rate = Number(ttsRateRef.current || 1.0);
      u.lang = String(ttsLangRef.current || "en-IN");

      setIsSpeaking(true);
      u.onend = () => setIsSpeaking(false);
      u.onerror = () => setIsSpeaking(false);

      window.speechSynthesis.speak(u);
      console.log("[TTS] rate/lang", u.rate, u.lang);
    } catch {
      setIsSpeaking(false);
    }
  }

  function cleanupRecordingStream() {
    try {
      const stream = recordingStreamRef.current;
      if (stream && typeof stream.getTracks === "function") {
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {}
        });
      }
    } catch {}

    recordingStreamRef.current = null;
  }

  function resetVoiceRecording() {
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch {}

    cleanupRecordingStream();

    try {
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
      }
    } catch {}

    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    setIsRecording(false);
    setRecordedAudioUrl("");
    setMicError("");
  }

  async function startVoiceRecording() {
    try {
      setMicError("");

      if (!navigator?.mediaDevices?.getUserMedia) {
        setMicError("Microphone recording is not supported in this browser.");
        return;
      }

      // Stop TTS before recording so the model voice is not captured.
      stopTTS();

      // Clear previous recording before starting a new one.
      try {
        if (recordedAudioUrl) {
          URL.revokeObjectURL(recordedAudioUrl);
        }
      } catch {}
      setRecordedAudioUrl("");
      recordingChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        try {
          const blob = new Blob(recordingChunksRef.current, {
            type: "audio/webm",
          });
          const url = URL.createObjectURL(blob);
          setRecordedAudioUrl(url);
          setVoiceStep(VOICE_STEPS.COMPARE);
        } catch {
          setMicError("Could not prepare your recording. Please try again.");
        } finally {
          cleanupRecordingStream();
          setIsRecording(false);
        }
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("[FluentVoice] microphone error", err);
      cleanupRecordingStream();
      setIsRecording(false);

      if (
        err?.name === "NotAllowedError" ||
        err?.name === "PermissionDeniedError"
      ) {
        setMicError(
          "Microphone permission was denied. Please allow microphone access and try again.",
        );
      } else {
        setMicError(
          "Could not start recording. Please check your microphone and try again.",
        );
      }
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const recorder = new MediaRecorder(stream);
      setMediaRecorder(recorder);

      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setUserRecording(url);
        setVoiceStep(VOICE_STEPS.COMPARE);
      };

      recorder.start();
      setAudioChunks(chunks);
    } catch (err) {
      alert("Microphone access denied");
    }
  }

  function stopRecording() {
    if (mediaRecorder) {
      mediaRecorder.stop();
    }
  }

  function stopVoiceRecording() {
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      setIsRecording(false);
      cleanupRecordingStream();
    }
  }

  function playMyRecording() {
    try {
      if (!recordedAudioUrl) return;
      const audio = new Audio(recordedAudioUrl);
      audio.play().catch(() => {
        setMicError("Could not play your recording. Please try again.");
      });
    } catch {
      setMicError("Could not play your recording. Please try again.");
    }
  }

  function seededShuffle(arr, seedStr) {
    const a = [...arr];
    // Simple deterministic hash
    let seed = 0;
    const s = String(seedStr ?? "0");
    for (let i = 0; i < s.length; i++)
      seed = (seed * 31 + s.charCodeAt(i)) >>> 0;

    // Deterministic “random” generator
    let x = seed || 123456789;
    const rand = () => {
      x ^= x << 13;
      x ^= x >>> 17;
      x ^= x << 5;
      return ((x >>> 0) % 1000000) / 1000000;
    };

    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  // -------------------
  // refs
  // -------------------
  const correctSoundRef = useRef(null);
  const wrongSoundRef = useRef(null);

  const xpInFlightKeysRef = useRef(new Set());

  const [ttsLang, setTtsLang] = useState("en-IN"); // default accent

  // -------------------
  // state
  // -------------------
  const [currentIndex, setCurrentIndex] = useState(0);

  const [tiles, setTiles] = useState([]);
  const [answer, setAnswer] = useState([]);

  const [attempts, setAttempts] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | wrong | correct | reveal
  const [showHint, setShowHint] = useState(false);
  const [wrongIndexes, setWrongIndexes] = useState([]);

  const [lessonExercises, setLessonExercises] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isComplete, setIsComplete] = useState(false);

  const quickStartStartedRef = useRef(false);
  const quickStartCompletedRef = useRef(false);

  const [loadError, setLoadError] = useState("");
  const [sessionTarget] = useState(null); // use all available questions in the lesson

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completionXp, setCompletionXp] = useState(0);
  const [sessionXpEarned, setSessionXpEarned] = useState(0);
  const [sessionXpResolved, setSessionXpResolved] = useState(false);
  const completionBonusRequestedRef = useRef(false);
  const [completionMode, setCompletionMode] = useState("typing");

  // typing/cloze shared input state
  const [selectedOption, setSelectedOption] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState("");

  // server-driven UI
  const [earnedXP, setEarnedXP] = useState(0);
  const [showXPToast, setShowXPToast] = useState(false);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState("");

  const [xpToastPhase, setXpToastPhase] = useState("hidden"); // hidden | enter | shown | exit

  const totalQuestions = Array.isArray(lessonExercises)
    ? lessonExercises.length
    : 0;

  const [suppressEmpty, setSuppressEmpty] = useState(false);

  // 🔊 Audio (TTS)
  const [revealEnglish, setRevealEnglish] = useState(false);
  const [ttsRate, setTtsRate] = useState(1.0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const ttsRateRef = useRef(ttsRate);
  const ttsLangRef = useRef(ttsLang);

  const [audioVariant, setAudioVariant] = useState("repeat"); // "repeat" | "dictation"

  const [audioGateOpen, setAudioGateOpen] = useState(false);
  const audioGateTimerRef = useRef(null);

  const [completeModeAvail, setCompleteModeAvail] = useState({
    reorder: undefined,
    audio: undefined,
  });

  // ===============================
  // FLUENT VOICE STATE ENGINE
  // ===============================

  const VOICE_STEPS = {
    LISTEN: "listen",
    REPEAT: "repeat",
    SHADOW: "shadow",
    RECORD: "record",
    COMPARE: "compare",
    DONE: "done",
  };

  const [voiceStep, setVoiceStep] = useState(VOICE_STEPS.LISTEN);
  const [userRecording, setUserRecording] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);

  const location = useLocation();
  const lessonIdFromQuery = Number(
    new URLSearchParams(location.search).get("lessonId"),
  );

  const lid = Number(lessonIdFromQuery ?? lessonId ?? 0);

  useEffect(() => {
    quickStartStartedRef.current = false;
    quickStartCompletedRef.current = false;
  }, [safeMode, lid, difficulty, audioVariant, location.search]);

  useEffect(() => {
    setIsComplete(false);
    setShowCompleteModal(false);
    setLoadError("");

    // 🔥 RESET VOICE ENGINE PER QUESTION CHANGE
    setVoiceStep(VOICE_STEPS.LISTEN);
    setAudioChunks([]);
    setUserRecording(null);
  }, [safeMode, lid, difficulty]);

  const xpInFlightRef = useRef(false);

  const audioSubmitRef = useRef(new Set()); // per-question "already submitted"
  const [audioSubmitting, setAudioSubmitting] = useState(false); // UX: disable button while saving

  // 🎙 Fluent Voice recording state — browser memory only, no backend upload
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState("");
  const [micError, setMicError] = useState("");

  const nextIndex = currentIndex + 1;
  const resumeIndexRef = useRef(null);
  const resumeAppliedRef = useRef(false);
  const resumeWantedRef = useRef(false);

  // ✅ Derived (must be AFTER useState declarations)
  const current =
    Array.isArray(lessonExercises) && typeof currentIndex === "number"
      ? (lessonExercises[currentIndex] ?? null)
      : null;

  // ✅ Derived data (single source of truth)
  const expected = current?.expected ?? {};

  const expectedWords = asArr(expected.words ?? expected.tokens);

  const expectedAnswer = String(
    expected.answer ?? expected.correct ?? expected.expected ?? "",
  ).trim();

  // ✅ NOW it's safe (expectedAnswer + current exist)
  const englishFull = String(expectedAnswer || current?.expected || "").trim();

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // For reorder: correctOrder (array) OR fallback to words OR split expectedAnswer
  const correctOrderArr =
    Array.isArray(expected.correctOrder) && expected.correctOrder.length
      ? expected.correctOrder
      : expectedWords.length
        ? expectedWords
        : expectedAnswer
          ? expectedAnswer.split(/\s+/)
          : [];

  // Typing hint-only word bank (SCRAMBLED, stable per question)
  const typingWordBankBase =
    Array.isArray(expectedWords) && expectedWords.length
      ? expectedWords
      : expectedAnswer
        ? expectedAnswer.split(/\s+/)
        : [];

  const typingWordBank =
    safeMode === "typing"
      ? seededShuffle(
          typingWordBankBase,
          current?.id ?? `${lid}:${currentIndex}`,
        )
      : typingWordBankBase;

  const playClick = () => {
    const audio = new Audio("/sounds/xp.mp3");
    audio.volume = 0.3; // Gentle feedback
    audio.play().catch(() => {});
  };

  function openAudioGateAfter(ms = 1800) {
    setAudioGateOpen(false);
    if (audioGateTimerRef.current) clearTimeout(audioGateTimerRef.current);
    audioGateTimerRef.current = setTimeout(() => {
      setAudioGateOpen(true);
    }, ms);
  }

  function resetAudioGate() {
    setAudioGateOpen(false);
    if (audioGateTimerRef.current) clearTimeout(audioGateTimerRef.current);
    audioGateTimerRef.current = null;
  }

  function triggerXPToast(xp) {
    const n = Number(xp || 0);
    if (!n) return;

    setEarnedXP(n);
    setShowXPToast(true);
    setXpToastPhase("enter");

    window.clearTimeout(window.__fjXpToastShownTimer);
    window.clearTimeout(window.__fjXpToastExitTimer);
    window.clearTimeout(window.__fjXpToastHideTimer);

    window.__fjXpToastShownTimer = window.setTimeout(() => {
      setXpToastPhase("shown");
    }, 50);

    window.__fjXpToastExitTimer = window.setTimeout(() => {
      setXpToastPhase("exit");
    }, 1800);

    window.__fjXpToastHideTimer = window.setTimeout(() => {
      setShowXPToast(false);
      setXpToastPhase("hidden");
    }, 2150);
  }

  useEffect(() => {
    audioSubmitRef.current = new Set();
  }, [lessonIdFromQuery, lessonId, audioVariant, safeMode]);

  useEffect(() => {
    ttsRateRef.current = ttsRate;
  }, [ttsRate]);
  useEffect(() => {
    ttsLangRef.current = ttsLang;
  }, [ttsLang]);

  useEffect(() => {
    if (safeMode !== "audio") return;
    const v = new URLSearchParams(location.search).get("variant");
    if (v === "repeat" || v === "dictation") setAudioVariant(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeMode, location.search]);

  useEffect(() => {
    console.log("[UI] status/earnedXP/toast", {
      safeMode,
      status,
      earnedXP,
      showXPToast,
    });
  }, [safeMode, status, earnedXP, showXPToast]);

  useEffect(() => {
    if (safeMode !== "audio") return;
    stopTTS();
    setRevealEnglish(false);

    if (audioVariant === "repeat") {
      resetVoiceRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, safeMode, audioVariant]);

  useEffect(() => {
    return () => {
      try {
        resetVoiceRecording();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------
  // effects
  // -------------------

  // 🔁 Smart Resume (auto, exact lesson + mode)
  useEffect(() => {
    try {
      const last = JSON.parse(
        localStorage.getItem("fj_last_session") || "null",
      );
      if (!last) return;

      // Must match current lesson + mode
      const sameLesson = String(last.lessonId) === String(lessonId);
      const sameMode = String(last.mode) === String(safeMode);

      let sameVariant = true;
      if (String(safeMode) === "audio") {
        const v = String(audioVariant || "");
        const lastV = String(last?.variant || "");
        // If last session had no variant, we allow resume (backward compatible)
        // If it has a variant, it must match.
        sameVariant = !lastV || lastV === v;
      }

      if (!sameLesson || !sameMode || !sameVariant) return;

      if (last?.questionIndex != null) {
        setCurrentIndex(Number(last.questionIndex) || 0);
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, safeMode]);

  useEffect(() => {
    if (!resumeAppliedRef.current) return;

    if (isComplete) return;
    if (totalQuestions > 0 && currentIndex >= totalQuestions) return;

    try {
      const session = {
        lessonId: Number(lid) || 0,
        difficulty: String(difficulty || "beginner"),
        mode: String(fetchMode || safeMode || ""),
        questionIndex: Number(currentIndex) || 0,
        updatedAt: Date.now(),
        ...(String(fetchMode || safeMode) === "audio"
          ? { variant: audioVariant }
          : {}),
      };
    } catch {}
  }, [
    lid,
    difficulty,
    fetchMode,
    safeMode,
    currentIndex,
    audioVariant,
    isComplete,
    totalQuestions,
  ]);

  useEffect(() => {
    const done =
      isComplete || (totalQuestions > 0 && currentIndex >= totalQuestions);
    if (!done) return;

    try {
      const total = totalQuestions || lessonExercises?.length || 0;

      const session = {
        lessonId: Number(lid) || 0,
        difficulty: String(difficulty || "beginner"),
        mode: String(fetchMode || safeMode || ""),
        index: "done",
        questionIndex: total,
        total,
        updatedAt: Date.now(),
        ...(String(fetchMode || safeMode) === "audio"
          ? { variant: audioVariant }
          : {}),
      };
    } catch {
      // ignore
    }
  }, [
    lid,
    difficulty,
    fetchMode,
    safeMode,
    audioVariant,
    isComplete,
    totalQuestions,
    currentIndex,
    lessonExercises?.length,
  ]);

  const isSessionDone =
    isComplete || (totalQuestions > 0 && currentIndex >= totalQuestions);

  useEffect(() => {
    if (!isStrictQuickStart || !isSessionDone) return;
    if (quickStartCompletedRef.current) return;

    quickStartCompletedRef.current = true;
    sendQuickStartAction(
      "Quick Start Completed",
      safeMode,
      safeMode === "audio" ? audioVariant : "",
    );
  }, [isStrictQuickStart, isSessionDone, safeMode, audioVariant]);

  useEffect(() => {
    if (!isSessionDone) return;

    const search = new URLSearchParams(location.search);
    if (!lid) return;

    let cancelled = false;

    (async () => {
      try {
        const reorderOk = await hasExercises(lid, "reorder");
        const audioOk = await hasExercises(lid, "audio");

        if (cancelled) return;

        setCompleteModeAvail({
          reorder:
            reorderOk === true ||
            reorderOk === "AUTH" ||
            reorderOk === "PAYWALL",
          audio:
            audioOk === true || audioOk === "AUTH" || audioOk === "PAYWALL",
        });
      } catch {
        if (!cancelled) setCompleteModeAvail({ reorder: true, audio: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSessionDone, location.search]);

  useEffect(() => {
    if (!isSessionDone) return;

    if (!lid) return;

    if (!isSessionDone) return;

    const search = new URLSearchParams(location.search);
    if (!lid) return;
    if (!progressUserId) return;

    const mode = String(fetchMode || safeMode || "").toLowerCase();
    if (!mode) return;

    // Only track the modes we care about in the hub
    const allowed = ["typing", "reorder", "audio", "cloze"];
    if (!allowed.includes(mode)) return;

    // When session is done, mark THIS mode complete (numbers)
    if (!totalQuestions || Number(totalQuestions) <= 0) return;
    try {
      writeProgress(progressUserId, lid, mode, {
        completed: Number(totalQuestions || 0),
        total: Number(totalQuestions || 0),
        updatedAt: Date.now(),
      });
    } catch {
      // ignore
    }
  }, [isSessionDone, location.search, fetchMode, safeMode, totalQuestions]);

  useEffect(() => {
    // Mode/lesson changed → reset completion + cursor so UI leaves Session Complete
    setIsComplete(false);
    setShowCompleteModal(false);

    // Reset the question cursor so completion condition can't stay true
    setCurrentIndex(0);

    // Force practice UI to re-load
    setStatus("loading");

    // Drop old exercises so derived totals recompute cleanly
    setLessonExercises([]);

    // Clear any prior error
    setLoadError(null);
  }, [safeMode, lid, difficulty, audioVariant, location.search]);

  useEffect(() => {
    setLessonExercises([]);
    loadLessonBatch();
  }, [safeMode, lid, difficulty, audioVariant, location.search]);

  // Keep total question count stored for LessonDetail progress summary
  useEffect(() => {
    if (!lid) return;
    if (!progressUserId) return;
    if (!lessonExercises || lessonExercises.length === 0) return;

    // store total for both supported modes (typing/reorder)
    writeProgress(progressUserId, lid, safeMode, {
      total: lessonExercises.length,
      updatedAt: Date.now(),
    });
    persistBackendLessonModeProgress({
      lessonId: lid,
      mode: safeMode,
      completed: 0,
      total: lessonExercises.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lid, safeMode, lessonExercises?.length, progressUserId]);

  useEffect(() => {
    initQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, safeMode, lessonExercises]);

  useEffect(() => {
    // 1) If URL has q=, it always wins
    const qRaw = search.get("q");
    if (qRaw != null) {
      const qNum = Number(qRaw);
      if (Number.isFinite(qNum) && qNum >= 0) {
        setCurrentIndex(qNum);
        return;
      }
    }

    // 2) Else, try last session
    try {
      const last = JSON.parse(
        localStorage.getItem("fj_last_session") || "null",
      );
      if (!last) {
        setCurrentIndex(0);
        return;
      }

      const sameLesson = Number(last.lessonId) === Number(lid);
      const sameDiff =
        String(last.difficulty || "beginner") ===
        String(difficulty || "beginner");
      const sameMode =
        String(last.mode || "").toLowerCase() ===
        String(fetchMode || safeMode || "").toLowerCase();

      if (sameLesson && sameDiff && sameMode) {
        const idx = Number(last.questionIndex);
        setCurrentIndex(Number.isFinite(idx) && idx >= 0 ? idx : 0);
        return;
      }
    } catch {
      // ignore
    }

    // 3) Default fresh start
    setCurrentIndex(0);
  }, [lid, difficulty, safeMode, fetchMode]);

  useEffect(() => {
    try {
      const last = JSON.parse(
        localStorage.getItem("fj_last_session") || "null",
      );
      if (!last) return;

      const sameLesson = Number(last.lessonId) === Number(lid);
      const sameDiff =
        String(last.difficulty || "beginner") ===
        String(difficulty || "beginner");
      const sameMode =
        String(last.mode || "").toLowerCase() ===
        String(fetchMode || safeMode || "").toLowerCase();

      if (!sameLesson || !sameDiff || !sameMode) return;

      const idx = Number(last.questionIndex);
      if (Number.isFinite(idx) && idx >= 0) {
        setCurrentIndex(idx);
      }
    } catch {}
  }, [lid, difficulty, fetchMode, safeMode]);

  useEffect(() => {
    // reset
    resumeIndexRef.current = null;
    resumeWantedRef.current = false;

    // 1) URL q= wins
    const qRaw = search.get("q");
    if (qRaw != null) {
      const qNum = Number(qRaw);
      if (Number.isFinite(qNum) && qNum >= 0) {
        resumeIndexRef.current = qNum;
        resumeWantedRef.current = true;
        return;
      }
    }

    // 2) Else use last session (if it matches this lesson/difficulty/mode)
    try {
      const last = JSON.parse(
        localStorage.getItem("fj_last_session") || "null",
      );
      if (!last) return;

      const sameLesson = Number(last.lessonId) === Number(lid);
      const sameDiff =
        String(last.difficulty || "beginner") ===
        String(difficulty || "beginner");
      const sameMode =
        String(last.mode || "").toLowerCase() ===
        String(fetchMode || safeMode || "").toLowerCase();

      if (!sameLesson || !sameDiff || !sameMode) return;

      const idx = Number(last.questionIndex);
      if (Number.isFinite(idx) && idx >= 0) {
        resumeIndexRef.current = idx;
        resumeWantedRef.current = true;
      }
    } catch {}
  }, [lid, difficulty, fetchMode, safeMode]);

  useEffect(() => {
    track("practice_view", {
      lessonId: Number(lid) || 0,
      difficulty,
      mode: safeMode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lid, difficulty, safeMode]);

  useEffect(() => {
    if (!isComplete) return;
    track("session_complete", {
      lessonId: Number(lid) || 0,
      difficulty,
      mode: safeMode,
      answered: Number(currentIndex) + 1,
      total: Number(totalQuestions || lessonExercises?.length || 0),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  // 🔊 Initialize sounds once
  useEffect(() => {
    correctSoundRef.current = new Audio("/sounds/correct.mp3");
    wrongSoundRef.current = new Audio("/sounds/wrong.mp3");
  }, []);

  const playCorrect = async () => {
    try {
      const a = correctSoundRef.current;
      if (!a) return;
      a.currentTime = 0;
      await a.play();
    } catch (e) {
      console.log("[SFX] correct blocked/failed", e);
    }
  };

  const playWrong = async () => {
    try {
      const a = wrongSoundRef.current;
      if (!a) return;
      a.currentTime = 0;
      await a.play();
    } catch (e) {
      console.log("[SFX] wrong blocked/failed", e);
    }
  };

  const onPickToken = (token) => {
    setReorderPicked((prev) => [...prev, token]);
    setReorderBank((prev) => prev.filter((x) => x.id !== token.id));
  };

  const onUnpickToken = (token) => {
    setReorderBank((prev) => [...prev, token]);
    setReorderPicked((prev) => prev.filter((x) => x.id !== token.id));
  };

  // AUTO NEXT after correct/reveal
  useEffect(() => {
    if (isComplete) return;

    if (status === "correct") {
      const timer = setTimeout(() => {
        loadNextQuestion();
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [status, isComplete]); // (loadNextQuestion is stable enough here)

  useEffect(() => {
    xpInFlightRef.current = false;
  }, [safeMode, currentIndex]);

  // ===== Universal XP event commit (inside component) =====
  async function awardXPEvent({
    questionId,
    xp,
    event,
    mode,
    lessonId: lid,
    exerciseId = null,
    meta = {},
    completedQuiz = false,
    attemptId,
    attemptNo,
    isCorrect,
    practiceType,
  }) {
    // 🔒 Prevent duplicate *same attempt* calls (but allow other XP events to run)
    const dedupeKey = String(
      attemptId ??
        `${mode}:${questionId ?? exerciseId ?? "na"}:${event}:${Number(attemptNo ?? 1) || 1}`,
    );

    if (xpInFlightKeysRef.current.has(dedupeKey)) {
      console.warn("[XP] skipped duplicate awardXPEvent (same key)", {
        dedupeKey,
        mode,
        event,
        questionId,
        exerciseId,
        attemptId,
        attemptNo,
      });
      return { ok: false, awarded: 0, skipped: true };
    }

    xpInFlightKeysRef.current.add(dedupeKey);

    try {
      const payload = {
        attemptId,
        attemptNo: Number(attemptNo ?? 1) || 1,

        // ✅ IMPORTANT: make questionId real, not undefined noise
        ...(questionId ? { questionId: String(questionId) } : {}),

        // xp: if you already compute xp elsewhere, keep it; else:
        xp: Number(xp) || 0,
        event,

        // keep your existing fields
        lessonId: Number(lid) || 0,
        practiceType: practiceType || mode,
        mode,
        exerciseId,

        meta: { lessonId: Number(lid) || 0, mode, exerciseId, ...meta },

        completedQuiz: !!completedQuiz,
        isCorrect:
          isCorrect === undefined ? event === "exercise_correct" : !!isCorrect,
      };

      const res = await api.post("/progress/update", payload);
      const data = res?.data ?? res;

      if (!data || data.ok !== true) {
        console.error("[XP] /progress/update not ok", {
          payload,
          response: data,
          message: data?.message,
          error: data?.error,
        });
        return { ok: false, awarded: 0, data };
      }

      const serverAwarded =
        Number(
          data?.xpAwarded ??
            data?.awarded ??
            data?.xpDelta ??
            data?.xp_delta ??
            0,
        ) || 0;

      return { ok: true, awarded: serverAwarded, data };
    } catch (err) {
      console.error("[XP] /progress/update failed", err);
      return { ok: false, awarded: 0, error: String(err?.message || err) };
    } finally {
      // 🔓 ALWAYS release this specific attempt lock
      xpInFlightKeysRef.current.delete(dedupeKey);
      console.log("[XP] awardXPEvent RELEASE", {
        dedupeKey,
        mode,
        event,
        questionId,
      });
    }
  } // <--- 🎯 This was the missing closing brace for awardXPEvent

  async function awardCompletionBonus(mode) {
    if (isStrictQuickStart) {
      return { ok: true, awarded: 0, skipped: true };
    }

    return awardXPEvent({
      xp: 300,
      event: "lesson_completed",
      mode,
      lessonId: Number(lessonId),
      completedQuiz: true,
    });
  }

  async function awardCompletionBonusOnce(mode) {
    if (completionBonusRequestedRef.current) {
      return { ok: true, awarded: 0, skipped: true };
    }

    completionBonusRequestedRef.current = true;

    const result = await awardCompletionBonus(mode);

    if (!result?.ok) {
      completionBonusRequestedRef.current = false;
      setSessionXpResolved(true);
      return result;
    }

    const awarded = Number(result?.awarded ?? 0) || 0;

    if (awarded > 0) {
      setCompletionXp(awarded);
      setSessionXpEarned((current) => current + awarded);
    }

    setSessionXpResolved(true);
    return result;
  }

  async function persistBackendLessonModeProgress({
    lessonId,
    mode,
    completed,
    total,
  }) {
    try {
      await saveLessonModeProgress({
        lessonId,
        mode,
        completed,
        total,
      });
    } catch (err) {
      console.error("[progress] backend lesson-mode persist failed", err);
    }
  }

  function normalizeForValidation(text) {
    return String(text || "")
      .toLowerCase()
      .trim()
      .replace(/[’‘‛ʼ]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/[.!?]+$/g, "")
      .replace(/\s+/g, " ");
  }

  // -------------------
  // helpers
  // -------------------
  function normalizeExercise(raw) {
    if (!raw) return null;

    // expected may be object or JSON string
    let expected = raw.expected;
    if (typeof expected === "string") {
      try {
        expected = JSON.parse(expected);
      } catch {
        expected = {};
      }
    }
    expected = expected || {};

    const id = raw.id;
    const promptTa = raw.promptTa || raw.prompt || raw.questionTa || "";
    const xp = Number(raw.xp || expected.xp || 0) || 0;

    const mode = String(
      expected.mode || expected.practiceType || raw.mode || "",
    )
      .toLowerCase()
      .trim();

    const words = Array.isArray(expected.words)
      ? expected.words
      : Array.isArray(expected.tokens)
        ? expected.tokens
        : Array.isArray(raw.words)
          ? raw.words
          : [];

    const answer =
      expected.answer ||
      expected.correct ||
      expected.expected ||
      expected.expectedAnswer ||
      raw.answer ||
      raw.expectedAnswer ||
      raw.expected_answer ||
      "";

    return {
      id,
      promptTa,
      xp,
      orderIndex: raw.orderIndex ?? 0,

      // ✅ top-level fields for older UI code
      mode,
      words,
      answer,

      // ✅ keep expected too
      expected: { ...expected, mode, words, answer },
    };
  }

  async function loadLessonBatch() {
    setLoading(true);
    setLoadError("");
    setSuppressEmpty(true);
    setTimeout(() => setSuppressEmpty(false), 800);

    const lessonIdNum = lid;

    try {
      const res = await api.get(
        `/quizzes/by-lesson/${lessonIdNum}?mode=${encodeURIComponent(fetchMode)}&difficulty=${encodeURIComponent(difficulty || "beginner")}`,
      );

      const data = res?.data ?? res;

      // ✅ PAYWALL redirect (fast path, no error flash)
      const isLocked =
        data?.ok === false &&
        (data?.code === "PAYWALL" ||
          String(data?.message || data?.error || "")
            .toLowerCase()
            .includes("locked"));

      if (isLocked) {
        const action = data?.nextAction;
        const from = action?.from || `lesson_${lessonIdNum}`;

        // If backend provided a redirect target, follow it
        if (action?.url) {
          const sep = String(action.url).includes("?") ? "&" : "?";
          const target = `${action.url}${sep}from=${encodeURIComponent(from)}`;

          // support external links too
          if (/^https?:\/\//i.test(target)) {
            window.location.href = target;
          } else {
            navigate(target, { replace: true });
          }
          return;
        }

        // fallback: go to practice hub (LessonDetail)
        navigate(`/lesson/${lessonIdNum}`, { replace: true });
        return;
      }

      // ✅ Backend shape: { ok:true, exercises:[...] }
      const exercises = Array.isArray(data?.exercises) ? data.exercises : [];

      const normalized = exercises
        .filter((raw) => {
          let expected = raw?.expected;

          if (typeof expected === "string") {
            try {
              expected = JSON.parse(expected);
            } catch {
              expected = {};
            }
          }

          expected = expected || {};

          const rawMode = String(
            expected?.mode ||
              expected?.practiceType ||
              raw?.mode ||
              raw?.practiceType ||
              raw?.practice_type ||
              "",
          )
            .toLowerCase()
            .trim();

          return rawMode === safeMode;
        })
        .map(normalizeExercise)
        .filter(Boolean)
        .sort(
          (a, b) => Number(a?.orderIndex || 0) - Number(b?.orderIndex || 0),
        );

      console.log("[Practice] safeMode:", safeMode);
      console.log("[Practice] exercises length:", exercises.length);
      console.log("[Practice] normalized length:", normalized.length);

      if (!normalized.length) {
        setLessonExercises([]);

        const prettyMode = safeMode.charAt(0).toUpperCase() + safeMode.slice(1);

        // Premium, actionable empty-state (no mysterious redirects)

        setLoadError(
          `No ${prettyMode} items for this lesson yet. Try ${fallbackMode} to keep your streak going.`,
        );

        return;
      }

      setLessonExercises(normalized);

      if (isStrictQuickStart && !quickStartStartedRef.current) {
        quickStartStartedRef.current = true;
        sendQuickStartAction(
          "Quick Start Started",
          safeMode,
          safeMode === "audio" ? audioVariant : "",
        );
      }

      // Only reset to Q1 on first load or hard restart, not on every reload
      setCurrentIndex((prev) => {
        if (!Number.isFinite(prev) || prev < 0) return 0;
        const max = Math.max(0, normalized.length - 1);
        return Math.min(prev, max);
      });
    } catch (e) {
      const status = e?.response?.status ?? e?.status ?? null;

      const data = e?.response?.data ?? e?.data ?? null;
      const code = data?.code ?? null;

      const msg = data?.message ?? data?.error ?? e?.message ?? "";

      if (
        status === 403 &&
        (code === "PAYWALL" || String(msg).toLowerCase().includes("locked"))
      ) {
        const action = data?.nextAction || null;
        const from = action?.from || `lesson_${lessonIdNum}`;

        if (action?.url) {
          const sep = String(action.url).includes("?") ? "&" : "?";
          const target = `${action.url}${sep}from=${encodeURIComponent(from)}`;

          if (/^https?:\/\//i.test(target)) {
            window.location.href = target;
          } else {
            navigate(target, { replace: true });
          }
          return;
        }

        navigate(`/lesson/${lessonIdNum}`, { replace: true });
        return;
      }

      console.error("[Practice] loadLessonBatch failed", e);
      setLoadError(
        status === 500 ? "Server error" : "Failed to load lesson exercises.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!Array.isArray(lessonExercises) || lessonExercises.length === 0) return;

    // If we wanted a resume and have an index, apply it once
    if (resumeWantedRef.current && resumeIndexRef.current != null) {
      const idx = Number(resumeIndexRef.current);
      const max = lessonExercises.length - 1;
      const clamped = Math.max(0, Math.min(idx, max));

      resumeIndexRef.current = null;
      resumeWantedRef.current = false;
    }

    // Now it's safe to start saving progress (prevents index 0 overwrite on refresh)
    resumeAppliedRef.current = true;
  }, [lessonExercises]);

  async function loadRandomOne() {
    setLoading(true);
    setLoadError("");
    try {
      const res = await api.get("/quizzes/random", {
        params: { mode: fetchMode, lessonId: lid },
      });
      const data = res?.data ?? res;
      const ex = normalizeExercise(data);

      if (!ex) {
        setLoadError("No exercise returned.");
        return;
      }

      setCurrentIndex(idx);
    } catch (e) {
      // ✅ 1) Detect PAYWALL and redirect immediately (no UI error flash)
      const status = e?.response?.status ?? e?.status ?? null;
      const code = e?.response?.data?.code ?? e?.data?.code ?? null;
      const msg =
        e?.response?.data?.message ?? e?.data?.message ?? e?.message ?? "";

      if (
        status === 403 &&
        (code === "PAYWALL" || String(msg).toLowerCase().includes("locked"))
      ) {
        // go to lesson detail page
        navigate(`/lesson/${lessonId}`, { replace: true });
        return; // ⛔ stops setLoadError below
      }

      // ✅ 2) Normal error handling
      console.error("[Practice] loadRandomOne failed", e);
      setLoadError("Failed to load a random exercise.");
    }
    setLoading(false);
  }

  function initQuiz() {
    setAttempts(0);
    setStatus("idle");
    setShowHint(false);
    setWrongIndexes([]);
    setTypedAnswer("");

    // initialize by mode
    if (safeMode === "reorder") {
      const shuffled = [...correctOrderArr].sort(() => Math.random() - 0.5);
      setTiles(shuffled);
      setAnswer([]);
    } else {
      setTiles([]);
      setAnswer([]);
    }
  }

  function hardResetAndRestart() {
    setShowCompleteModal(false);
    setIsComplete(false);
    setCompletionXp(0);
    setSessionXpEarned(0);
    setSessionXpResolved(false);
    completionBonusRequestedRef.current = false;

    setCurrentIndex(0);
    setStatus("idle");
    setFeedback("");
    setShowHint(false);
    setTypedAnswer("");
    setWrongIndexes([]);
    setRevealEnglish(false);
    setEarnedXP(0);
    setShowXPToast(false);
    setAnswer([]);
    setTiles([]);

    setAudioSubmitting(false);
    audioSubmitRef.current = new Set();
    stopTTS();
    resetAudioGate();

    loadLessonBatch();
  }

  function loadNextQuestion() {
    // reset transient UI for next question
    setEarnedXP(0);
    setShowXPToast(false);
    setStatus("idle");
    setFeedback("");
    setShowHint(false);
    setAttempts(0);
    setTypedAnswer("");
    setWrongIndexes([]);
    setRevealEnglish(false);

    setAnswer([]);
    setTiles([]);

    const total =
      sessionTarget == null
        ? lessonExercises?.length || 0
        : Math.min(lessonExercises?.length || 0, sessionTarget);
    const nextIndex = currentIndex + 1;

    const modeKey = safeMode === "audio" ? "audio" : safeMode;
    persistModeProgress(modeKey, nextIndex);

    // ✅ if this Next will finish the session, compute + store completion bonus now
    if (total > 0 && nextIndex >= total) {
      if (!completionBonusRequestedRef.current) {
        Promise.resolve(awardCompletionBonusOnce(safeMode)).catch(() => {});
      }
    }

    setCurrentIndex((prev) => {
      const next = prev + 1;

      if (total > 0 && next >= total) {
        setIsComplete(true);
      }

      return next;
    });
  }

  function addToAnswer(word) {
    if (status === "correct" || status === "reveal") return;
    setAnswer((prev) => [...prev, word]);
    setTiles((prev) => {
      const idx = prev.indexOf(word);
      if (idx === -1) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  }

  function playCorrectSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.value = 0.05;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    } catch (e) {
      // ignore if blocked
    }
  }

  function playSfx(name) {
    try {
      const audio = new Audio(`/sounds/${name}.mp3`);
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch {}
  }

  // ===== REORDER: check correctness =====
  const checkReorderAnswer = async () => {
    if (!current) return;
    if (status === "correct" || status === "reveal") return;

    // answer: your selected words state
    const userArr = toTextArray(answer);
    const correctArr = toTextArray(correctOrderArr);

    const isCorrect = arraysEqualStrict(userArr, correctArr);

    // highlight wrong positions (only when wrong)
    // highlight wrong positions (only when wrong)
    if (!isCorrect) {
      const wrong = [];
      const L = Math.max(userArr.length, correctArr.length);
      for (let i = 0; i < L; i++) {
        if ((userArr[i] ?? "") !== (correctArr[i] ?? "")) wrong.push(i);
      }
      setWrongIndexes(wrong);
      playSfx("wrong");
      setShowHint(true);

      // ✅ attempts-driven wrong → reveal flow (same as typing)
      setAttempts((a) => {
        const next = a + 1;

        if (next >= MAX_ATTEMPTS) {
          setStatus("reveal");
          setWrongIndexes([]); // optional: stop highlighting when revealing
        } else {
          setStatus("wrong");
        }

        return next;
      });
    } else {
      setWrongIndexes([]);
      setShowHint(false);
      setAttempts(0); // reset attempts after correct
      setStatus("correct");
    }

    if (isCorrect) {
      const xp = Number(current?.xp ?? 150) || 150;

      // 1) UI feedback immediately
      playSfx("correct");
      setStatus("correct");
      triggerXPToast(xp);

      // 2) Sync XP to backend in background
      awardXPEvent({
        xp,
        event: "exercise_correct",
        mode: "reorder",
        lessonId: Number(lessonId),
        exerciseId: current?.id,
      })
        .then((result) => {
          const awarded = Number(result?.awarded ?? 0) || 0;

          if (!result?.ok) {
            console.error("[XP] reorder: XP sync failed", result);
          } else if (awarded <= 0) {
            console.warn("[XP] reorder: XP already awarded or deduped", result);
          }
        })
        .catch((e) => {
          console.error("[XP] reorder awardXPEvent failed", e);
        });

      // 3) Completion bonus only on last question
      if (currentIndex + 1 >= totalQuestions) {
        awardCompletionBonus("reorder").catch((e) => {
          console.error("[XP] reorder completion bonus failed", e);
        });
      }

      // important: stop here; do NOT fall through to wrong
      return;
    }
  };

  //  �� Typing helper: tap a word chip to append into typedAnswer
  function addToTyped(word) {
    if (status === "correct" || status === "reveal") return;

    setTypedAnswer((prev) => {
      const t = String(prev || "").trimEnd();
      return (t ? t + " " : "") + word + " ";
    });
  }

  function handleTryAgain() {
    setAnswer([]);
    setWrongIndexes([]);
    setStatus("idle");
    setShowHint(false);
    setAttempts(0);

    if (safeMode === "typing") {
      setTypedAnswer("");
      return;
    }

    if (safeMode === "cloze") {
      setTypedAnswer("");
      return;
    }

    if (safeMode === "reorder") {
      const reshuffled = [...correctOrderArr].sort(() => Math.random() - 0.5);
      setTiles(reshuffled);
      setAnswer([]);
    }
  }

  function handleRepeatDoneOnly() {
    if (!current) return;

    if (!audioGateOpen) {
      setFeedback("▶ Tap Listen first, then repeat the sentence aloud.");
      return;
    }

    setFeedback("✅ Good. Now shadow the sentence with the audio.");
    setVoiceStep(VOICE_STEPS.RECORD);
  }

  async function handleAudioRepeated() {
    if (!current) return;
    if (status === "correct") return;

    const submitKey = `${audioVariant}:${current.id}`;

    // ✅ prevent double submit on same question
    if (audioSubmitRef.current.has(submitKey)) {
      return;
    }

    // ✅ anti-abuse: must press Play first
    if (!audioGateOpen) {
      setFeedback("▶ Tap Listen first, then click “I spoke it well ✅”");
      return;
    }

    // ✅ prevent spam clicks while request is running
    if (audioSubmitting) return;

    setAudioSubmitting(true);

    console.log("[AUDIO_REPEAT] clicked", {
      audioGateOpen,
      currentId: current?.id,
      status,
      inFlight: xpInFlightRef.current,
    });

    const attemptNumber = attempts + 1;

    try {
      // ✅ Instant UI feedback
      audioSubmitRef.current.add(submitKey);
      setStatus("correct");
      setVoiceStep(VOICE_STEPS.DONE);
      triggerXPToast(150);
      playSfx("correct");
      setFeedback("✅ Great speaking practice! +150 XP");

      const currentId = current && current.id ? current.id : "unknown";

      // ✅ Backend sync continues in background
      awardXPEvent({
        attemptId: `audio-repeat-${currentId}-${attemptNumber}`,
        attemptNo: attemptNumber,
        xp: 150,
        event: "exercise_correct",
        lessonId: lid || 0,
        mode: "audio",
        practiceType: "audio",
        exerciseId: current && current.id ? current.id : undefined,
        questionId: `repeat_${currentId}`,
        meta: { audioVariant: "repeat" },
        completedQuiz: false,
        isCorrect: true,
      })
        .then((result) => {
          const awarded = Number((result && result.awarded) || 0) || 0;

          if (!result || result.ok !== true) {
            console.error("[AUDIO/repeat] XP sync failed", result);
            return;
          }

          if (awarded <= 0) {
            console.warn(
              "[AUDIO/repeat] XP already awarded or deduped",
              result,
            );
          }
        })
        .catch((err) => {
          console.error("[AUDIO/repeat] awardXPEvent failed", err);
        });

      resetAudioGate();

      resetAudioGate();

      const completedNow = Math.min(lessonExercises.length, currentIndex + 1);

      writeProgress(progressUserId, lid, "audio", {
        total: lessonExercises.length,
        completed: completedNow,
        updatedAt: Date.now(),
      });

      persistBackendLessonModeProgress({
        lessonId: lid,
        mode: "audio",
        completed: completedNow,
        total: lessonExercises.length,
      });

      setTimeout(() => {
        loadNextQuestion();
        setRevealEnglish(false);
        setShowHint(false);
        setWrongIndexes([]);
        setTypedAnswer("");
        setStatus("idle");
        setFeedback("");
      }, 700);
    } catch (err) {
      console.error("[audio/repeat] award failed", err);
      setStatus("wrong");
      setFeedback("❌ Try again");
    } finally {
      setAudioSubmitting(false);
    }
  }

  async function checkAnswer() {
    if (safeMode === "reorder") {
      return checkReorderAnswer();
    }

    if (!current) return;
    if (status === "correct") return;

    // ⌨️ TYPING validation
    if (
      safeMode === "typing" ||
      (safeMode === "audio" && audioVariant === "dictation")
    ) {
      const normalize = (s) =>
        String(s || "")
          .trim()
          .toLowerCase()
          .replace(/[’‘‛ʼ]/g, "'")
          .replace(/[“”]/g, '"')
          .replace(/\bI'm\b/g, "i am")
          .replace(/\byou're\b/g, "you are")
          .replace(/\bhe's\b/g, "he is")
          .replace(/\bshe's\b/g, "she is")
          .replace(/\bit's\b/g, "it is")
          .replace(/\bwe're\b/g, "we are")
          .replace(/\bthey're\b/g, "they are")
          .replace(/\bisn't\b/g, "is not")
          .replace(/\baren't\b/g, "are not")
          .replace(/\bwasn't\b/g, "was not")
          .replace(/\bweren't\b/g, "were not")
          .replace(/\bcan't\b/g, "cannot")
          .replace(/\bdon't\b/g, "do not")
          .replace(/\bdoesn't\b/g, "does not")
          .replace(/\bdidn't\b/g, "did not")
          .replace(/\bwon't\b/g, "will not")
          .replace(/\bwouldn't\b/g, "would not")
          .replace(/\bcouldn't\b/g, "could not")
          .replace(/\bshouldn't\b/g, "should not")
          .replace(/[.!?]+$/g, "")
          .replace(/\s+/g, " ");

      const target =
        String(expectedAnswer || "").trim() ||
        String((correctOrder || []).join(" ")).trim();

      const user = typedAnswer;

      if (!normalize(user)) {
        setStatus("wrong");
        setShowHint(true);
        playSfx("wrong");
        return;
      }

      const attemptNumber = attempts + 1;

      const qid =
        safeMode === "audio" && audioVariant === "dictation"
          ? `dict_${current.id}`
          : `typing_${current.id}`;

      if (normalize(user) === normalize(target)) {
        const isPostReveal = status === "reveal";

        setStatus("correct");
        setWrongIndexes([]);
        playSfx("correct");

        if (isPostReveal) {
          persistModeProgress(
            safeMode === "audio" ? "audio" : "typing",
            currentIndex + 1,
          );
          return;
        }

        const xpDelta = Number((current && current.xp) || 150) || 150;

        // ✅ Instant XP feedback
        triggerXPToast(xpDelta);

        const xpModeForTyping = safeMode === "audio" ? "audio" : "typing";

        const xpMetaForTyping =
          safeMode === "audio" && audioVariant === "dictation"
            ? { audioVariant: "dictation" }
            : {};

        // ✅ Backend sync continues in background
        awardXPEvent({
          attemptId: `${qid}-${attemptNumber}`,
          attemptNo: attemptNumber,
          questionId: qid,
          xp: xpDelta,
          event: "exercise_correct",
          mode: xpModeForTyping,
          practiceType: xpModeForTyping,
          lessonId: Number(lessonId),
          exerciseId: current ? current.id : undefined,
          meta: xpMetaForTyping,
          completedQuiz: false,
          isCorrect: true,
        })
          .then((result) => {
            const awarded = Number((result && result.awarded) || 0) || 0;

            if (!result || result.ok !== true) {
              console.error("[XP] typing: XP sync failed", result);
              return;
            }

            if (awarded <= 0) {
              console.warn(
                "[XP] typing: XP already awarded or deduped",
                result,
              );
            }
          })
          .catch((err) => {
            console.error("[XP] typing awardXPEvent failed", err);
          });

        if (progressUserId) {
          const prev = readProgress(progressUserId, lid, "typing");
          const completedNow = Math.max(
            Number(prev?.completed || 0),
            currentIndex + 1,
          );
          const totalNow = Number(prev?.total || lessonExercises?.length || 0);

          writeProgress(progressUserId, lid, "typing", {
            completed: completedNow,
            total: totalNow,
            updatedAt: Date.now(),
          });
          persistBackendLessonModeProgress({
            lessonId: lid,
            mode: "typing",
            completed: completedNow,
            total: totalNow,
          });
        }

        const isLastQuestion =
          currentIndex >= (lessonExercises?.length || 0) - 1;

        if (isLastQuestion) {
          try {
            await awardCompletionBonusOnce(
              safeMode === "audio" && audioVariant === "dictation"
                ? "audio"
                : "typing",
            );

            setCompletionMode(
              safeMode === "audio" && audioVariant === "dictation"
                ? "audio"
                : "typing",
            );

            const willHitSessionComplete =
              totalQuestions > 0 && currentIndex + 1 >= totalQuestions;

            if (!willHitSessionComplete) {
              setShowCompleteModal(true);
            }
          } catch (err) {
            console.error("[XP] completion bonus failed", err);
          }
        }
        return;
      }

      // wrong typing
      wrongSoundRef.current?.play?.();
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      setStatus("wrong");
      setShowHint(true);

      if (nextAttempts >= MAX_ATTEMPTS) {
        setStatus("reveal");
      }
      return;
    }

    // 🧩 CLOZE validationsetEarnedXP
    if (safeMode === "cloze") {
      const normalizeWord = (s) =>
        String(s || "")
          .trim()
          .toLowerCase()
          .replace(/[’‘‛ʼ]/g, "'")
          .replace(/[“”]/g, '"')
          .replace(/\s+/g, " ");

      const expectedMissing = normalizeWord(cloze?.missingWord);
      const got = normalizeWord(typedAnswer);

      if (!got) {
        setStatus("wrong");
        setShowHint(true);
        return;
      }

      // ✅ Accept either the missing word OR the full sentence
      const gotWords = got.split(" ").filter(Boolean);
      const expectedWordOk = expected && got === expected;

      const expectedFull = normalizeWord(String(cloze?.full || ""));
      const expectedFullOk = expectedFull && got === expectedFull;

      // If user typed full sentence with same number of words,
      // validate the blank position word
      const positionalOk =
        Array.isArray(cloze?.words) &&
        typeof cloze?.index === "number" &&
        gotWords.length === cloze.words.length &&
        normalizeWord(gotWords[cloze.index]) === expected;

      const isCorrect =
        expected && (expectedWordOk || expectedFullOk || positionalOk);

      if (isCorrect) {
        const attemptNumber = attempts + 1;

        setStatus("correct");
        setFeedback("50� Correct!");

        // ✅ SFX
        try {
          playCorrect?.();
        } catch {}

        if (isCorrect) {
          const attemptNumber = attempts + 1;

          setStatus("correct");
          setFeedback("50� Correct!");

          // ✅ SFX
          try {
            playCorrect?.();
          } catch {}

          const xpDelta = Number(current && current.xp ? current.xp : 80) || 80;

          // ✅ Instant XP feedback
          triggerXPToast(xpDelta);

          // ✅ Backend sync continues safely
          awardXPEvent({
            xp: xpDelta,
            event: "exercise_correct",
            mode: xpMode,
            lessonId: Number(lessonId),
            exerciseId: current ? current.id : undefined,
          })
            .then((result) => {
              const awarded = Number((result && result.awarded) || 0) || 0;

              if (!result || result.ok !== true) {
                console.error("[XP] cloze: XP sync failed", result);
                return;
              }

              if (awarded <= 0) {
                console.warn(
                  "[XP] cloze: XP already awarded or deduped",
                  result,
                );
              }
            })
            .catch((err) => {
              console.error("[XP] cloze awardXPEvent failed", err);
            });

          // i�� Update progress (Cloze)
          if (progressUserId) {
            writeProgress(progressUserId, lessonId, "cloze", {
              total: lessonExercises.length,
              completed: Math.min(lessonExercises.length, currentIndex + 1),
              updatedAt: Date.now(),
            });
          }

          setTimeout(() => {
            loadNextQuestion();
          }, 900);

          return;
        }

        // i�� Update progress (Cloze)
        if (progressUserId) {
          writeProgress(progressUserId, lessonId, "cloze", {
            total: lessonExercises.length,
            completed: Math.min(lessonExercises.length, currentIndex + 1),
            updatedAt: Date.now(),
          });
        }

        setTimeout(() => {
          loadNextQuestion(); // loadNextQuestion will handle resets
        }, 900);

        return;
      }

      // wrong cloze
      setStatus("wrong");
      setFeedback(`❌ Correct answer: "${cloze?.missingWord || ""}"`);
      setEarnedXP(0);
      try {
        playWrong?.();
      } catch {}
      setShowHint(true);
      return;
    }

    // ✅ REORDER validation
    if (incorrect.length === 0) {
      const attemptNumber = attempts + 1;

      setWrongIndexes([]);
      setStatus("correct");

      try {
        const xpDelta = Number(current?.xp ?? 100) || 100;

        // ✅ Show XP immediately
        triggerXPToast(xpDelta);
        playSfx("correct");
        setFeedback("✅ Correct!");

        // ✅ Sync XP in background
        awardXPEvent({
          attemptId: `reorder_${current?.id}-${attemptNumber}`,
          attemptNo: attemptNumber,
          questionId: `reorder_${current?.id}`,
          xp: xpDelta,
          event: "exercise_correct",
          mode: "reorder",
          practiceType: "reorder",
          lessonId: Number(lessonId),
          exerciseId: current?.id,
          meta: {},
          completedQuiz: false,
          isCorrect: true,
        })
          .then((result) => {
            const awarded = Number(result?.awarded ?? 0) || 0;

            if (result?.ok && awarded > 0) {
              setSessionXpEarned((current) => current + awarded);
            }

            if (!result?.ok) {
              console.error("[XP] reorder sync failed", result);
            } else if (awarded <= 0) {
              console.warn("[XP] reorder already awarded or deduped", result);
            }
          })
          .catch((err) => {
            console.error("[XP] reorder awardXPEvent failed", err);
          });

        const isLastQuestion =
          currentIndex >= (lessonExercises?.length || 0) - 1;

        if (isLastQuestion) {
          awardCompletionBonusOnce("reorder")
            .then(() => {
              setCompletionMode("reorder");

              const willHitSessionComplete =
                totalQuestions > 0 && currentIndex + 1 >= totalQuestions;

              if (!willHitSessionComplete) {
                setShowCompleteModal(true);
              }
            })
            .catch((err) => {
              console.error("[XP] reorder completion bonus failed", err);
            });
        }
      } catch (err) {
        console.error("[XP] reorder instant feedback failed", err);
      }

      // Update lesson progress (Reorder)
      if (progressUserId) {
        const prev = readProgress(progressUserId, lid, "reorder");
        const completedNow = Math.max(
          Number(prev?.completed || 0),
          currentIndex + 1,
        );
        const totalNow = Number(prev?.total || lessonExercises?.length || 0);

        writeProgress(progressUserId, lid, "reorder", {
          completed: completedNow,
          total: totalNow,
          updatedAt: Date.now(),
        });
        persistBackendLessonModeProgress({
          lessonId: lid,
          mode: "reorder",
          completed: completedNow,
          total: totalNow,
        });
      }
    }

    // WRONG
    wrongSoundRef.current?.play?.();
    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);
    setStatus("wrong");
    setShowHint(true);
    setWrongIndexes(incorrect);

    if (nextAttempts >= MAX_ATTEMPTS) {
      setStatus("reveal");
      setEarnedXP(0);
    }
  }

  // -------------------
  // mode guard (MVP)
  // -------------------
  if (
    safeMode !== "reorder" &&
    safeMode !== "typing" &&
    safeMode !== "cloze" &&
    safeMode !== "audio"
  ) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-2">
          Practice: {uiFor(safeMode).title}
        </h1>
        <p className="text-gray-600">
          This mode is coming next. For now, use <b>{uiFor("reorder").title}</b>{" "}
          or <b>{uiFor("typing").title}</b>.
        </p>
      </div>
    );
  }

  function safeStopAudioAndUnlock() {
    // Audio safety no-ops if not in audio mode
    try {
      setAudioSubmitting(false);
    } catch {}
    try {
      if (audioSubmitRef?.current) audioSubmitRef.current = new Set();
    } catch {}
    try {
      stopTTS?.();
    } catch {}
    try {
      resetVoiceRecording?.();
    } catch {}
    try {
      resetAudioGate?.();
    } catch {}
  }

  function hardResetThenNavigate(nextMode, nextVariant) {
    const sp = new URLSearchParams(location.search);
    const lid = sp.get("lessonId"); // SOURCE OF TRUTH
    if (!lid) return;

    const diffRaw = String(sp.get("difficulty") || "").toLowerCase();
    const difficulty = diffRaw === "intermediate" ? "intermediate" : "beginner";

    const mode = String(nextMode || "").toLowerCase();
    if (!mode) return;

    // Build target URL FIRST
    let target = "";

    if (mode === "audio") {
      const qs = addQuickStartParams(new URLSearchParams(sp));
      qs.set("lessonId", String(lid));
      qs.set("difficulty", difficulty);
      qs.set("variant", nextVariant || "repeat");
      qs.set("restart", String(Date.now()));
      target = `/practice/audio?${qs.toString()}`;
    } else {
      const qs = addQuickStartParams(new URLSearchParams(sp));
      qs.set("lessonId", String(lid));
      qs.set("difficulty", difficulty);
      qs.delete("variant");
      qs.set("restart", String(Date.now()));
      target = `/practice/${mode}?${qs.toString()}`;
    }

    // Navigate FIRST (so button doesn't disappear without redirect)
    navigate(target, { replace: true });

    // Then reset state on next tick (prevents stale modal / audio lock)
    setTimeout(() => {
      try {
        setIsComplete(false);
      } catch {}
      try {
        setShowCompleteModal(false);
      } catch {}
      try {
        setCompletionXp(0);
        setSessionXpEarned(0);
        setSessionXpResolved(false);
        completionBonusRequestedRef.current = false;
      } catch {}

      safeStopAudioAndUnlock();
    }, 0);
  }

  const MODE_ACCENT = {
    reorder: {
      bar: "bg-indigo-500",
      soft: "bg-indigo-50",
      border: "border-indigo-200",
      text: "text-indigo-700",
    },
    typing: {
      bar: "bg-amber-500",
      soft: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
    },
    audio: {
      bar: "bg-emerald-500",
      soft: "bg-emerald-50",
      border: "border-emerald-200",
      text: "text-emerald-700",
    },
  };
  const A = MODE_ACCENT[safeMode] || {
    bar: "bg-slate-500",
    soft: "bg-slate-50",
    border: "border-slate-200",
    text: "text-slate-700",
  };

  // -------------------
  // completion
  // -------------------
  //    �� SESSION COMPLETE (10 questions) — engagement loop
  if (isComplete || (totalQuestions > 0 && currentIndex >= totalQuestions)) {
    const search = new URLSearchParams(location.search);
    const lid = Number(search.get("lessonId") || 0);
    const nextLessonId = lid ? lid + 1 : null;

    const diffRaw = String(search.get("difficulty") || "").toLowerCase();
    const difficulty = diffRaw === "intermediate" ? "intermediate" : "beginner";
    const base = difficulty === "intermediate" ? "/i" : "/b";

    const currentLessonNumber = Number(lid || 1);

    if (isStrictQuickStart) {
      const currentExperience =
        safeMode === "audio" ? `${safeMode}:${audioVariant}` : safeMode;
      const quickStartModes = [
        { title: "Reorder", mode: "reorder", variant: "" },
        { title: "Typing", mode: "typing", variant: "" },
        { title: "Audio Repeat", mode: "audio", variant: "repeat" },
        { title: "Dictation", mode: "audio", variant: "dictation" },
      ].filter(({ mode, variant }) => {
        const experience = mode === "audio" ? `${mode}:${variant}` : mode;
        return experience !== currentExperience;
      });

      const practiseAnotherMode = (mode, variant) => {
        sendQuickStartAction("Quick Start Mode Selected", mode, variant);

        const params = addQuickStartParams(
          new URLSearchParams({
            lessonId: String(QUICK_START_DAY_NUMBER),
            difficulty: "beginner",
            restart: String(Date.now()),
          }),
        );
        if (variant) params.set("variant", variant);

        navigate(`/practice/${mode}?${params.toString()}`);
      };

      return (
        <div className="min-h-screen bg-slate-950 px-4 py-10 text-white sm:px-6">
          <main className="mx-auto max-w-3xl">
            <section className="overflow-hidden rounded-[2rem] border border-emerald-300/30 bg-gradient-to-br from-emerald-600 via-teal-700 to-slate-900 p-7 text-center shadow-2xl sm:p-10">
              <p className="text-sm font-black uppercase tracking-[0.2em] text-emerald-100">
                Starter challenge complete
              </p>
              <h1 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
                You completed your first 10 sentences!
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-lg font-semibold leading-8 text-emerald-50">
                This was only the beginning. FluencyJet contains a guided
                120-lesson path to help you keep building confident English.
              </p>

              <button
                type="button"
                onClick={() => {
                  sendQuickStartAction(
                    "Continue to Free Lessons Clicked",
                    safeMode,
                    safeMode === "audio" ? audioVariant : "",
                  );
                  continueToFreeLessons();
                }}
                className="mt-8 w-full rounded-2xl bg-white px-6 py-4 text-lg font-black text-emerald-800 shadow-lg transition hover:-translate-y-0.5"
              >
                Continue to My 3 Free Lessons →
              </button>
            </section>

            <section className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-xl font-black">
                Practise These Sentences in Another Mode
              </h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {quickStartModes.map(({ title, mode, variant }) => (
                  <button
                    key={`${mode}-${variant || "default"}`}
                    type="button"
                    onClick={() => practiseAnotherMode(mode, variant)}
                    className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left font-black transition hover:bg-white/20"
                  >
                    {title} →
                  </button>
                ))}
              </div>
            </section>
          </main>
        </div>
      );
    }

    if (showLesson1ConversionComplete) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8">
        <main className="mx-auto max-w-3xl">
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.08)]">
            <div className="h-2 w-full bg-indigo-500" />

            <div className="p-6 sm:p-9">
              <div className="text-center">
                <p className="text-sm font-black uppercase tracking-[0.22em] text-indigo-600">
                  Your First FluencyJet Workout
                </p>

                <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
                  🎉 Great job!
                </h1>

                <p className="mx-auto mt-4 max-w-xl text-lg font-extrabold leading-8 text-slate-800 sm:text-xl">
                  You completed the Lesson 1 Quick English workout.
                </p>

                <div className="mx-auto mt-6 grid max-w-xl grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-indigo-50 p-4">
                    <div className="text-2xl font-black text-indigo-700">
                      {totalQuestions}
                    </div>

                    <div className="mt-1 text-xs font-black uppercase tracking-wider text-indigo-500">
                      Sentences completed
                    </div>
                  </div>

                  <div className="rounded-2xl bg-amber-50 p-4">
                    <div className="text-xl font-black text-amber-700 sm:text-2xl">
                      Progress saved
                    </div>

                    <div className="mt-1 text-xs font-black uppercase tracking-wider text-amber-600">
                      Workout completed
                    </div>
                  </div>
                </div>
              </div>

              <section className="mt-8 rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 p-6 text-white shadow-xl sm:p-8">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-100">
                  Recommended Next Step
                </p>

                <h2 className="mt-3 text-2xl font-black">
                  Continue Lesson 1
                </h2>

                <p className="mt-3 font-semibold leading-7 text-indigo-50">
                  Watch the short video and learn how to use the sentences you
                  just practised.
                </p>

                <button
                  type="button"
                  className="mt-6 w-full rounded-2xl bg-white px-6 py-5 text-lg font-black text-indigo-700 shadow-lg transition hover:-translate-y-0.5 hover:bg-indigo-50 active:scale-[0.99]"
                  onClick={() => {
                    track("practice_cta_clicked", {
                      lessonId: 1,
                      difficulty: "beginner",
                      mode: "reorder",
                      cta: "watch_lesson_1_video",
                      source: onboardingSource,
                    });

                    navigate(
                      `/b/lesson/1?difficulty=beginner&onboarding=1&source=${encodeURIComponent(
                        onboardingSource,
                      )}`,
                      { replace: true },
                    );
                  }}
                >
                  Watch Lesson 1 Video →
                </button>
              </section>
            </div>
          </section>
        </main>
      </div>
    );
  }

  const goToQuiz = (mode, variant = "") => {
      const qs = new URLSearchParams();
      qs.set("lessonId", String(currentLessonNumber));
      qs.set("difficulty", difficulty);
      qs.set("q", "0");
      qs.set("restart", String(Date.now()));

      if (variant) {
        qs.set("variant", variant);
      }

      navigate(`/practice/${mode}?${qs.toString()}`);
    };

    const quizCards = [
      {
        title: "Quick English",
        subtitle: "Arrange the sentence",
        actionText: "Try now →",
        icon: "🧩",
        dot: "bg-violet-500",
        card: "border-violet-200 bg-violet-50 text-violet-900 hover:border-violet-300",
        onClick: () => goToQuiz("reorder"),
      },
      {
        title: "Audio Repeat",
        subtitle: "Listen and speak",
        actionText: "Try now →",
        icon: "🎧",
        dot: "bg-emerald-500",
        card: "border-emerald-200 bg-emerald-50 text-emerald-900 hover:border-emerald-300",
        onClick: () => goToQuiz("audio", "repeat"),
      },
      {
        title: "Grammar Genius",
        subtitle: "Type the answer",
        actionText: "Try now →",
        icon: "⌨️",
        dot: "bg-orange-500",
        card: "border-orange-200 bg-orange-50 text-orange-900 hover:border-orange-300",
        onClick: () => goToQuiz("typing"),
      },
      {
        title: "Listening Quiz",
        subtitle: "Listen and type",
        actionText: "Try now →",
        icon: "✍️",
        dot: "bg-rose-500",
        card: "border-rose-200 bg-rose-50 text-rose-900 hover:border-rose-300",
        onClick: () => goToQuiz("audio", "dictation"),
      },
    ];

    const storedUser = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "null");
      } catch {
        return null;
      }
    })();

    const progressUserId =
      auth?.user?.id ||
      auth?.user?.email ||
      auth?.id ||
      auth?.email ||
      storedUser?.id ||
      storedUser?.email ||
      null;

    const completedQuizCount = ["typing", "reorder", "audio"].filter(
      (modeName) =>
        pct(readProgress(progressUserId, currentLessonNumber, modeName)) >= 100,
    ).length;

    const quizCompletionText =
      completedQuizCount >= 3
        ? `Lesson ${currentLessonNumber}: All 3 Quizzes Completed 🎉`
        : completedQuizCount === 2
          ? `Lesson ${currentLessonNumber}: 2 Quizzes Completed`
          : completedQuizCount === 1
            ? `Lesson ${currentLessonNumber}: 1 Quiz Completed`
            : `Lesson ${currentLessonNumber}: Quiz Completed`;

    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
            {/* Accent bar (mode-based) */}
            <div
              className={`h-2 w-full ${MODE_ACCENT?.[safeMode]?.bar || "bg-slate-500"}`}
            />

            <div className="p-6 sm:p-9">
              <div className="text-center">
                <div className="text-sm sm:text-base font-black uppercase tracking-[0.22em] text-slate-600">
                  Session complete
                </div>
                <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
                  🎉 Great job!
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-lg sm:text-xl font-extrabold leading-8 text-slate-800">
                  {quizCompletionText}
                </p>
              </div>

              {/* CTAs */}
              <div className="mt-7 grid gap-3">
                {/* CTA 1: Back to same lesson */}
                <button
                  type="button"
                  className="w-full rounded-2xl border border-slate-300 bg-slate-100 px-6 py-5 text-lg font-black text-slate-800 shadow-sm transition hover:bg-slate-200"
                  onClick={() => {
                    track("practice_cta_clicked", {
                      lessonId: Number(lid) || 0,
                      difficulty,
                      mode: safeMode,
                      cta: "back_to_lesson",
                    });

                    navigate(
                      `${base}/lesson/${lid || 1}?difficulty=${encodeURIComponent(difficulty)}`,
                      { replace: true },
                    );
                  }}
                >
                  ← Back to Lesson {currentLessonNumber}
                </button>

                {/* CTA 2: Practice this lesson more */}
                <div className="mt-6">
                  <div className="mb-4 text-sm sm:text-base font-black uppercase tracking-[0.18em] text-slate-600">
                    Practice Lesson {currentLessonNumber} More
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {quizCards.map((card) => (
                      <button
                        key={card.title}
                        type="button"
                        onClick={() => {
                          playClick();
                          card.onClick();
                        }}
                        className={[
                          "w-full rounded-2xl border p-6 text-left shadow-sm transition-all",
                          "hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99]",
                          card.card,
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-4">
                          <div className="text-3xl leading-none">
                            {card.icon}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={`h-3 w-3 rounded-full ${card.dot}`}
                              />
                              <h3 className="text-lg font-black">
                                {card.title}
                              </h3>
                            </div>

                            <p className="mt-2 text-base font-bold opacity-80">
                              {card.subtitle}
                            </p>

                            <p className="mt-4 text-base font-black">
                              {card.actionText}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA 4: Continue to next lesson */}
                {nextLessonId ? (
                  <button
                    type="button"
                    className="w-full rounded-2xl border border-indigo-300 bg-indigo-100 px-6 py-5 text-lg font-black text-indigo-800 shadow-sm transition hover:bg-indigo-200"
                    onClick={() => {
                      track("practice_cta_clicked", {
                        lessonId: Number(lid) || 0,
                        difficulty,
                        mode: safeMode,
                        cta: "continue_next_lesson",
                        nextLessonId,
                      });

                      navigate(
                        `${base}/lesson/${nextLessonId}?difficulty=${encodeURIComponent(
                          difficulty,
                        )}&autostart=1`,
                        { replace: true },
                      );
                    }}
                  >
                    Continue to Lesson {nextLessonId} →
                  </button>
                ) : null}

                {/* CTA 5: Leaderboard */}
                <button
                  type="button"
                  className="w-full rounded-2xl border border-amber-300 bg-amber-100 px-6 py-5 text-base font-black text-amber-800 shadow-sm transition hover:bg-amber-200"
                  onClick={() => navigate("/leaderboard")}
                >
                  🏆 View Leaderboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const exLen = Array.isArray(lessonExercises) ? lessonExercises.length : 0;

  // -------------------
  // empty state (no exercises)
  // -------------------
  if (
    !suppressEmpty &&
    hasLoadedOnce &&
    !loading &&
    (loadError || exLen === 0)
  ) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="text-lg font-semibold">No questions yet</div>

          <div className="mt-1 text-sm text-gray-600">
            {loadError ||
              `No exercises found. mode=${fetchMode} lessonId=${lid}`}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/b/lesson/${lesson?.day_number || lid}?difficulty=${encodeURIComponent(difficulty)}`,
                )
              }
              className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Back to Lesson
            </button>

            <button
              type="button"
              onClick={() => loadLessonBatch()}
              className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Retry
            </button>

            <button
              type="button"
              onClick={() => {
                const nextMode = fallbackMode;

                hardResetThenNavigate(nextMode);
              }}
              className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Try other mode
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------
  // empty / error / no current (single source of truth)
  // -------------------
  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="text-lg font-semibold text-red-600">{loadError}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/b/lesson/${lesson?.day_number || lid}?difficulty=${encodeURIComponent(difficulty)}`,
                )
              }
              className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Back to Lesson
            </button>
            <button
              type="button"
              onClick={() => loadLessonBatch()}
              className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (
    hasLoadedOnce &&
    !loading &&
    (loadError || lessonExercises.length === 0)
  ) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="text-lg font-semibold">No questions yet</div>
          <div className="mt-1 text-sm text-gray-600">
            This lesson doesn’t have practice items yet.
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/b/lesson/${lesson?.day_number || lid}?difficulty=${encodeURIComponent(difficulty)}`,
                )
              }
              className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Back to Lesson
            </button>
            <button
              type="button"
              onClick={() => loadLessonBatch()}
              className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => {
                playClick();
                const nextMode = fallbackMode;

                hardResetThenNavigate(nextMode);
              }}
              className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Try {uiFor(nextMode).title} →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!suppressEmpty && hasLoadedOnce && !loading && !current) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-2">No question loaded yet</h1>
        <p className="text-gray-600">Please retry.</p>
        <button
          type="button"
          onClick={() => loadLessonBatch()}
          className="mt-4 rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          Retry
        </button>
      </div>
    );
  }

  function AudioVariantToggle({ audioVariant, onGoVariant }) {
    return (
      <div className="inline-flex items-center rounded-2xl border border-slate-300 bg-indigo-100 p-1 shadow-inner">
        <button
          type="button"
          onClick={() => onGoVariant("repeat")}
          className={`min-w-[136px] rounded-xl px-4 py-2 text-sm font-semibold transition ${
            audioVariant === "repeat"
              ? "bg-slate-900 text-white shadow-sm"
              : "text-slate-700 hover:bg-white"
          }`}
        >
          Speak Practice
        </button>

        <button
          type="button"
          onClick={() => onGoVariant("dictation")}
          className={`min-w-[96px] rounded-xl px-4 py-2 text-sm font-semibold transition ${
            audioVariant === "dictation"
              ? "bg-slate-900 text-white shadow-sm"
              : "text-slate-700 hover:bg-white"
          }`}
        >
          Dictation
        </button>
      </div>
    );
  }

  // ===============================
  // Sticky Bottom CTA (MVP-safe)
  // ===============================
  const BTN_PRIMARY_BASE =
    "w-full rounded-2xl px-4 py-3 text-base font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50";

  const STICKY_PRIMARY_BG = {
    reorder: "bg-indigo-600 hover:bg-indigo-700",
    typing: "bg-amber-600 hover:bg-amber-700",
    audio: "bg-emerald-600 hover:bg-emerald-700",
  };

  const BTN_PRIMARY_MODE_BG =
    safeMode === "reorder"
      ? "bg-indigo-600 hover:bg-indigo-700"
      : safeMode === "typing"
        ? "bg-amber-600 hover:bg-amber-700"
        : safeMode === "audio"
          ? "bg-emerald-600 hover:bg-emerald-700"
          : "bg-slate-900 hover:bg-slate-950";

  const stickyPrimaryBg =
    safeMode === "reorder"
      ? "bg-indigo-600 hover:bg-indigo-700"
      : safeMode === "typing"
        ? "bg-amber-600 hover:bg-amber-700"
        : safeMode === "audio"
          ? "bg-emerald-600 hover:bg-emerald-700"
          : "bg-slate-900 hover:bg-slate-950";

  const BTN_SECONDARY =
    "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50";

  function StickyCTABar({ cfg }) {
    if (!cfg?.show) return null;

    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/85 backdrop-blur shadow-[0_-8px_30px_rgba(15,23,42,0.08)]">
        <div
          className={`h-[3px] w-full ${MODE_ACCENT?.[safeMode]?.bar || "bg-slate-500"}`}
        />
        <div className="mx-auto max-w-3xl px-4 pt-3 pb-[env(safe-area-inset-bottom)]">
          {cfg.hintText ? (
            <div className="mb-2 text-xs font-semibold text-slate-500">
              {cfg.hintText}
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            {cfg.secondary?.length ? (
              <div className="flex flex-wrap gap-2">
                {cfg.secondary.map((s, idx) => (
                  <button
                    key={`${s.label}-${idx}`}
                    type="button"
                    onClick={s.onClick}
                    className={BTN_SECONDARY}
                    disabled={!!s.disabled}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ) : (
              <div />
            )}

            <div className="ml-auto w-full max-w-[260px]">
              <button
                type="button"
                onClick={cfg.primary.onClick}
                className={`${BTN_PRIMARY_BASE} ${
                  STICKY_PRIMARY_BG?.[safeMode] ||
                  "bg-slate-900 hover:bg-slate-950"
                }`}
                disabled={!!cfg.primary.disabled}
              >
                {cfg.primary.label}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function getStickyCtaConfig({
    show,
    lid,
    difficulty,
    safeMode,
    status,
    audioVariant,
    audioGateOpen,
    typedAnswer,
    reorderAnswerLen,
    track,

    onCheckReorder,
    onTryAgain,
    onNext,
    onSubmit,
    onReveal,
    onClearTyping,
    onAudioRepeated,
    onPlayAudio,
    onResetAudio,

    onRepeatDone,
    voiceStep,
    isRecording,
    recordedAudioUrl,
    startVoiceRecording,
    stopVoiceRecording,
  }) {
    if (!show) return { show: false, primary: null, secondary: [] };

    const basePayload = {
      lid,
      difficulty,
      mode: safeMode,
      variant: safeMode === "audio" ? audioVariant : undefined,
      status,
    };

    const firePrimary = (label, fn) => () => {
      try {
        track?.("sticky_primary_click", { ...basePayload, label });
      } catch {}
      fn?.();
    };

    const fireSecondary = (label, fn) => () => {
      try {
        track?.("sticky_secondary_click", { ...basePayload, label });
      } catch {}
      fn?.();
    };

    // ===== REORDER =====
    if (safeMode === "reorder") {
      if (status === "idle") {
        return {
          show: true,
          primary: {
            label: "Check Answer",
            onClick: firePrimary("Check Answer", onCheckReorder),
            disabled: !(reorderAnswerLen > 0),
          },
          secondary: [
            { label: "Reset", onClick: fireSecondary("Reset", onTryAgain) },
          ],
        };
      }
      if (status === "wrong") {
        return {
          show: true,
          primary: {
            label: "Try again",
            onClick: firePrimary("Try again", onTryAgain),
          },
          secondary: [],
        };
      }
      return {
        show: true,
        primary: { label: "Next", onClick: firePrimary("Next", onNext) },
        secondary: [],
      };
    }

    // ===== TYPING + AUDIO DICTATION =====
    const isTypingLike =
      safeMode === "typing" ||
      (safeMode === "audio" && audioVariant === "dictation");

    if (isTypingLike) {
      const trimmed = String(typedAnswer || "").trim();
      const canSubmit = trimmed.length > 0;

      if (status === "correct") {
        return {
          show: true,
          primary: { label: "Next", onClick: firePrimary("Next", onNext) },
          secondary: [],
        };
      }

      if (status === "reveal") {
        return {
          show: true,
          primary: {
            label: "Try it now",
            onClick: firePrimary("Try it now", onSubmit),
            disabled: !canSubmit,
          },
          secondary: [],
        };
      }

      const secondaryRaw = [
        {
          label: "Show Answer",
          onClick: fireSecondary("Show Answer", onReveal),
        },
      ];

      // Typing & Dictation: keep sticky clean (Clear stays in the card header)
      return {
        show: true,
        primary: {
          label: "Submit",
          onClick: firePrimary("Submit", onSubmit),
          disabled: !canSubmit,
        },
        secondary: secondaryRaw,
      };
    }

    // ===== AUDIO REPEAT / SHADOW =====
    if (safeMode === "audio" && audioVariant === "repeat") {
      if (voiceStep === VOICE_STEPS.LISTEN) {
        return {
          show: true,
          primary: {
            label: "Listen first ▶",
            onClick: firePrimary("Listen first ▶", onPlayAudio),
            disabled: false,
          },
          secondary: [],
          hintText: "Step 1: Listen to the model sentence",
        };
      }

      if (voiceStep === VOICE_STEPS.REPEAT) {
        return {
          show: true,
          primary: {
            label: "I repeated it ✅",
            onClick: firePrimary("I repeated it ✅", onRepeatDone),
            disabled: !audioGateOpen,
          },
          secondary: [],
          hintText: audioGateOpen
            ? "Step 2: Repeat aloud, then continue"
            : "Click Listen first ✅",
        };
      }

      if (voiceStep === VOICE_STEPS.RECORD) {
        return {
          show: true,
          primary: {
            label: isRecording ? "Stop Recording ■" : "Start Recording 🎙",
            onClick: firePrimary(
              isRecording ? "Stop Recording ■" : "Start Recording 🎙",
              isRecording ? stopVoiceRecording : startVoiceRecording,
            ),
            disabled: false,
          },
          secondary: [],
          hintText: "Step 4: Record your voice",
        };
      }

      if (voiceStep === VOICE_STEPS.COMPARE) {
        return {
          show: true,
          primary: {
            label: "I spoke it well ✅",
            onClick: firePrimary("I spoke it well ✅", onAudioRepeated),
            disabled: !recordedAudioUrl || audioSubmitting,
          },
          secondary: [],
          hintText: "Step 4: Compare your voice with the model",
        };
      }

      return {
        show: true,
        primary: {
          label: "Next",
          onClick: firePrimary("Next", onAudioRepeated),
          disabled: audioSubmitting,
        },
        secondary: [],
      };
    }

    return { show: false, primary: null, secondary: [] };
  }

  // ===== Sticky CTA visibility (ACTIVE question only) =====
  const stickyShow = !!current && !loading && !loadError && !showCompleteModal;

  // Safe audio play/reset wrappers (do not crash if functions are absent)
  const playAudioSafe = () => {
    const text = String(expectedAnswer || englishFull || "").trim();
    try {
      if (typeof speakTTS === "function") speakTTS(text);
      else if (typeof speak === "function") speak(text);
    } catch {}
  };

  const resetAudioSafe = () => {
    try {
      if (typeof stopTTS === "function") stopTTS();
      else if (typeof stop === "function") stop();
    } catch {}
    try {
      resetVoiceRecording();
    } catch {}
    try {
      setRevealEnglish(false);
    } catch {}
    try {
      if (typeof resetAudioGate === "function") resetAudioGate();
    } catch {}
  };

  const stickyCfg = getStickyCtaConfig({
    show: !!stickyShow,
    lid,
    difficulty,
    safeMode,
    status,
    audioVariant,
    audioGateOpen: !!audioGateOpen,
    typedAnswer,
    reorderAnswerLen: Array.isArray(answer) ? answer.length : 0,
    track,

    onCheckReorder: checkReorderAnswer,
    onTryAgain: handleTryAgain,
    onNext: loadNextQuestion,
    onSubmit: checkAnswer,
    onReveal: () => setStatus("reveal"),
    onClearTyping: () => setTypedAnswer(""),
    onAudioRepeated: handleAudioRepeated,
    onPlayAudio: playAudioSafe,
    onResetAudio: resetAudioSafe,

    onRepeatDone: handleRepeatDoneOnly,
    voiceStep,
    isRecording,
    recordedAudioUrl,
    startVoiceRecording,
    stopVoiceRecording,
  });

  const setAudioVariantInUrl = (v) => {
    const sp = new URLSearchParams(location.search);
    sp.set("variant", v);
    navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
  };

  {
    /* ⚡ SPEED DEMON TOAST */
  }
  <div
    id="speed-toast"
    className="fixed top-24 left-1/2 -translate-x-1/2 z-[600] pointer-events-none opacity-0 transition-all duration-500"
  >
    <div className="bg-amber-400 text-slate-900 px-6 py-3 rounded-full font-black text-xs uppercase tracking-widest shadow-xl flex items-center gap-3 border-4 border-white animate-bounce">
      <span> � SPEED DEMON!</span>
      <span className="bg-white/30 px-2 py-0.5 rounded-lg">FAST AS FLASH</span>
    </div>
  </div>;

  // -------------------
  // UI
  // -------------------
  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <PracticeHeader
        lid={lid}
        difficulty={difficulty}
        mode={safeMode}
        currentIndex={currentIndex}
        total={totalQuestions || lessonExercises?.length || 0}
        streakText={`${Number(streak || 0)}-day streak`}
        onBack={() => {
          if (isStrictQuickStart) {
            const params = new URLSearchParams({
              source: "free_live_class",
              segment: quickStartSegment,
            });
            navigate(`/quick-start?${params.toString()}`);
            return;
          }

          const rawDifficulty = String(difficulty || "").toLowerCase();
          const normalizedDifficulty =
            rawDifficulty === "intermediate" ? "intermediate" : "basic";

          const lessonBase =
            normalizedDifficulty === "intermediate" ? "/i" : "/b";

          const modeKey = safeMode === "audio" ? "audio" : safeMode;
          const resolved = status === "correct" || status === "reveal";

          persistModeProgress(
            modeKey,
            resolved ? currentIndex + 1 : currentIndex,
          );

          const hubHref = `${lessonBase}/lesson/${lid || 1}?difficulty=${encodeURIComponent(
            normalizedDifficulty,
          )}`;

          window.location.href = hubHref;
        }}
      />
      {import.meta.env.DEV ? (
        <div className="mx-auto max-w-xl px-4 pt-3 text-center text-xs text-slate-400">
          urlMode: <b>{String(urlMode)}</b> | activeMode: <b>{activeMode}</b> |
          safeMode: <b>{safeMode}</b> | lid: <b>{String(lid)}</b>
        </div>
      ) : null}
      <PromptCard tamil={current?.promptTa || "—"} />
      {/* Hint */}
      {showHint && status !== "correct" && (
        <div className="mx-auto max-w-3xl px-4 pt-3">
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-sm font-semibold text-purple-800">
            Hint: Subject → Verb → Action
          </div>
        </div>
      )}
      {/* Main content wrapper (DO NOT close this here) */}
      <div
        className={`mx-auto max-w-4xl px-4 pt-4 ${stickyCfg.show ? "pb-40" : "pb-10"}`}
      >
        {/* 🧩 CLOZE UI */}
        {safeMode === "cloze" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-slate-900">
                Cloze Practice
              </h2>

              <button
                onClick={() => setTypedAnswer("")}
                className="text-xs px-3 py-1 rounded bg-slate-100 hover:bg-slate-200"
                disabled={status === "correct" || status === "reveal"}
              >
                Clear
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-2">
              Fill the missing word:
            </div>

            <div className="rounded-xl border bg-white p-4">
              <div className="text-lg font-semibold tracking-wide">
                {cloze?.masked || "____"}
              </div>

              <div className="mt-3">
                <input
                  value={typedAnswer}
                  onChange={(e) => setTypedAnswer(e.target.value)}
                  placeholder="Type ONLY the missing word (or type the full sentence)..."
                  className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  disabled={status === "correct" || status === "reveal"}
                />
              </div>

              <div className="flex items-center gap-3 mt-3">
                {!stickyCfg.show && (
                  <button
                    onClick={checkAnswer}
                    className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    disabled={
                      status === "correct" || status === "reveal" || !cloze
                    }
                  >
                    Submit
                  </button>
                )}

                <button
                  onClick={() => setStatus("reveal")}
                  className="px-4 py-2 rounded bg-yellow-500 text-white hover:bg-yellow-600"
                  disabled={status === "correct" || status === "reveal"}
                >
                  Show Answer
                </button>
              </div>

              {status === "reveal" && (
                <div className="mt-3 text-sm text-gray-700">
                  ✅ Answer:{" "}
                  <span className="font-semibold">{cloze?.missingWord}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ⌨️ TYPING UI */}
        {(safeMode === "typing" ||
          (safeMode === "audio" && audioVariant === "dictation")) && (
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_18px_45px_rgba(15,23,42,0.06)] sm:p-6">
            <div className={`h-1.5 w-full rounded-full ${A.bar}`} />

            <div className="mb-5 mt-3">
              <div className="min-w-0">
                <h2 className="text-[22px] font-black tracking-tight text-slate-950 sm:text-2xl">
                  {safeMode === "audio" && audioVariant === "dictation"
                    ? "Listening Dictation"
                    : uiFor(safeMode).title}
                </h2>

                <p className="mt-1 text-sm font-bold text-slate-500">
                  {safeMode === "audio" && audioVariant === "dictation"
                    ? "Listen carefully and type the sentence"
                    : "Type the full English sentence"}
                </p>

                {/* Status pills */}
                {status === "wrong" && (
                  <div className="mt-3 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700">
                    Try again
                  </div>
                )}

                {status === "reveal" && (
                  <div
                    className={`mt-3 inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${A.border} ${A.soft} ${A.text}`}
                  >
                    Answer shown — try typing it now
                  </div>
                )}

                {status === "correct" && (
                  <div className="mt-3 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                    Nice! ✅
                  </div>
                )}
              </div>

              <div className="mt-4 flex shrink-0 flex-wrap items-center gap-3">
                {safeMode === "audio" && audioVariant === "dictation" ? (
                  <AudioVariantToggle
                    audioVariant={audioVariant}
                    onGoVariant={(v) => setAudioVariantInUrl(v)}
                  />
                ) : null}

                <button
                  type="button"
                  onClick={() => setTypedAnswer("")}
                  className="shrink-0 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 hover:shadow"
                  disabled={status === "correct"}
                >
                  Clear
                </button>
              </div>
            </div>

            {/* 🔊 Audio Dictation Controls (only when audio + dictation) */}
            {safeMode === "audio" && audioVariant === "dictation" && (
              <div className="mb-5 rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4 shadow-inner">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                      Dictation controls
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-600">
                      Tap play once. Then type what you heard.
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        speakTTS(englishFull);
                        setVoiceStep(VOICE_STEPS.REPEAT);
                      }}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                      disabled={!englishFull}
                    >
                      <span className="text-base">▶</span>
                      {isSpeaking ? "Playing…" : "Play"}
                    </button>

                    <button
                      type="button"
                      onClick={() => stopTTS()}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      Stop
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Word Bank (hint only — not clickable) */}
            {safeMode === "typing" && (
              <div className="mb-5 rounded-[1.5rem] border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white p-5 shadow-sm">
                <div className="mb-3 text-xs font-black uppercase tracking-[0.12em] text-slate-500">
                  Word Bank <span className="text-slate-400">(Hint Only)</span>
                </div>

                <div className="flex flex-wrap gap-3">
                  {(typingWordBank || []).map((w, idx) => (
                    <span
                      key={`${w}_${idx}`}
                      className="select-none rounded-2xl border border-slate-300 bg-white px-5 py-2.5 text-[18px] font-extrabold text-slate-800 shadow-sm sm:text-[19px]"
                    >
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="rounded-[1.5rem] border border-indigo-100 bg-gradient-to-br from-indigo-50/70 via-white to-white p-4 shadow-inner">
              <textarea
                value={typedAnswer}
                onChange={(e) => setTypedAnswer(e.target.value)}
                placeholder="Type the full English sentence here..."
                className="min-h-[130px] w-full resize-none rounded-2xl border border-slate-200 bg-white px-5 py-4 text-[20px] font-bold leading-relaxed text-slate-950 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-100 sm:text-[22px]"
                rows={3}
                disabled={status === "correct"}
              />
            </div>

            <div className="mt-4 flex items-center gap-3">
              {!stickyCfg.show && (
                <button
                  onClick={checkAnswer}
                  className="rounded-2xl bg-orange-500 px-6 py-3 text-base font-black text-white shadow-lg shadow-orange-100 transition hover:bg-orange-600 active:scale-[0.98]"
                  disabled={status === "correct" || status === "reveal"}
                >
                  Submit
                </button>
              )}

              {!stickyCfg.show && (
                <button
                  onClick={() => setStatus("reveal")}
                  className="rounded-2xl border border-slate-200 bg-white px-6 py-3 text-base font-black text-slate-700 shadow-sm transition hover:bg-slate-50 hover:shadow"
                  disabled={status === "correct" || status === "reveal"}
                >
                  Show Answer
                </button>
              )}
            </div>
          </div>
        )}

        {safeMode === "audio" && audioVariant === "repeat" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className={`h-1 w-full rounded-t-2xl ${A.bar}`} />

            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl font-extrabold tracking-tight text-slate-900">
                  {uiFor("audio").title}
                </h2>

                <div className="mt-1 text-sm text-slate-600">
                  Listen, repeat aloud, record your voice, and compare it with
                  the model.
                </div>

                {/* Status pills */}
                {!audioGateOpen && status !== "correct" && (
                  <div className="mt-3 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                    Step 1: Listen first ✅
                  </div>
                )}

                {audioGateOpen && status !== "correct" && (
                  <div className="mt-3 inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-800">
                    Now repeat, shadow, record, and compare 🎙
                  </div>
                )}

                {status === "correct" && (
                  <div className="mt-3 inline-flex items-center rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-900">
                    Spoken practice completed ✅
                  </div>
                )}

                {/* Repeat / Dictation toggle */}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <AudioVariantToggle
                    audioVariant={audioVariant}
                    onGoVariant={(v) => setAudioVariantInUrl(v)}
                  />

                  <button
                    type="button"
                    onClick={() => {
                      stopTTS();
                      resetVoiceRecording();
                      setRevealEnglish(false);
                      resetAudioGate();
                    }}
                    className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Fluent Voice Practice
                  </div>
                  <div className="mt-2 text-sm font-bold text-slate-800">
                    Listen → Repeat → Record → Compare
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Goal: say the sentence clearly, naturally, and confidently.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setRevealEnglish((v) => !v)}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                    revealEnglish
                      ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  disabled={!current}
                >
                  {revealEnglish ? "Hide English" : "Reveal English"}
                </button>
              </div>

              {revealEnglish && (
                <div className="mt-4 rounded-2xl border border-indigo-200 bg-white p-4">
                  <div className="text-xs font-semibold text-indigo-700">
                    English
                  </div>
                  <div className="mt-2 text-lg font-bold text-slate-900">
                    {englishFull || "—"}
                  </div>
                </div>
              )}

              {/* Step buttons */}
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Step 1: Listen */}
                <div
                  className={`rounded-3xl border p-5 shadow-sm transition ${
                    voiceStep === VOICE_STEPS.LISTEN
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    Step 1
                  </div>

                  <div className="mt-1 text-lg font-extrabold text-slate-900">
                    Listen
                  </div>

                  <div className="mt-2 text-sm text-slate-600">
                    Listen to the model voice.
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      playClick();
                      openAudioGateAfter(1800);
                      speakTTS(englishFull);
                      setVoiceStep(VOICE_STEPS.REPEAT);
                    }}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
                    disabled={!englishFull}
                  >
                    ▶ Listen
                  </button>
                </div>

                {/* Step 2: Repeat */}
                <div
                  className={`rounded-3xl border p-5 shadow-sm transition ${
                    voiceStep === VOICE_STEPS.REPEAT
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    Step 2
                  </div>

                  <div className="mt-1 text-lg font-extrabold text-slate-900">
                    Repeat
                  </div>

                  <div className="mt-2 text-sm text-slate-600">
                    Repeat the sentence aloud.
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      playClick();
                      handleRepeatDoneOnly();
                    }}
                    className="mt-4 inline-flex w-full items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                    disabled={
                      !audioGateOpen || voiceStep === VOICE_STEPS.LISTEN
                    }
                  >
                    I Repeated It ✅
                  </button>
                </div>

                {/* Step 3: Record */}
                <div
                  className={`rounded-3xl border p-5 shadow-sm transition ${
                    voiceStep === VOICE_STEPS.RECORD
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    Step 3
                  </div>

                  <div className="mt-1 text-lg font-extrabold text-slate-900">
                    Record
                  </div>

                  <div className="mt-2 text-sm text-slate-600">
                    Record your voice clearly.
                  </div>

                  {!isRecording ? (
                    <button
                      type="button"
                      onClick={() => {
                        playClick();
                        startVoiceRecording();
                      }}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                      disabled={voiceStep !== VOICE_STEPS.RECORD}
                    >
                      🎙 Record
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        playClick();
                        stopVoiceRecording();
                      }}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-3 py-2.5 text-sm font-bold text-white hover:bg-rose-700"
                    >
                      ■ Stop
                    </button>
                  )}
                </div>

                {/* Step 4: Compare */}
                <div
                  className={`rounded-3xl border p-5 shadow-sm transition ${
                    voiceStep === VOICE_STEPS.COMPARE
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    Step 4
                  </div>

                  <div className="mt-1 text-lg font-extrabold text-slate-900">
                    Compare
                  </div>

                  <div className="mt-2 text-sm text-slate-600">
                    Compare with the model.
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      playClick();
                      playMyRecording();
                    }}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    disabled={!recordedAudioUrl}
                  >
                    ▶ My Voice
                  </button>
                </div>
              </div>

              {micError && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800">
                  {micError}
                </div>
              )}

              {recordedAudioUrl && (
                <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-sm font-bold text-emerald-900">
                    Recording ready ✅
                  </div>
                  <div className="mt-1 text-xs text-emerald-800">
                    Play your voice, compare it with the original, then try
                    again if needed.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        playClick();
                        speakTTS(englishFull);
                      }}
                      className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      disabled={!englishFull}
                    >
                      ▶ Original
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        playClick();
                        playMyRecording();
                      }}
                      className="rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                    >
                      ▶ My Voice
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        playClick();
                        resetVoiceRecording();
                      }}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      🔁 Try Again
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                <div className="mb-1 text-xs text-slate-500">Rate</div>
                <input
                  type="range"
                  min="0.6"
                  max="1.8"
                  step="0.1"
                  value={ttsRate}
                  onChange={(e) => setTtsRate(Number(e.target.value))}
                  className="w-full"
                />
                <div className="mt-1 text-xs font-semibold text-slate-700">
                  {ttsRate.toFixed(2)}
                </div>
              </label>

              <label className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                <div className="mb-1 text-xs text-slate-500">Accent</div>
                <select
                  value={ttsLang}
                  onChange={(e) => setTtsLang(e.target.value)}
                  className="w-full rounded-lg border px-2 py-2 text-sm"
                >
                  <option value="en-US">US</option>
                  <option value="en-GB">UK</option>
                  <option value="en-IN">India</option>
                </select>
              </label>

              <div className="flex items-end">
                {!stickyCfg.show && (
                  <button
                    type="button"
                    onClick={handleAudioRepeated}
                    className="w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                    disabled={!recordedAudioUrl || audioSubmitting}
                  >
                    {audioSubmitting ? "⏳ Saving..." : "I Spoke It Well ✅"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* REORDER UI */}
        {safeMode === "reorder" && (
          <ReorderExerciseCard
            title={uiFor("reorder").title}
            subtitle="Build the English sentence"
            answer={answer}
            tiles={tiles}
            wrongIndexes={wrongIndexes}
            status={status}
            accent={A}
            onTileClick={addToAnswer}
            footer={
              !stickyCfg.show && status === "idle" ? (
                <button
                  type="button"
                  onClick={checkReorderAnswer}
                  className="mt-5 w-full rounded-2xl bg-indigo-600 py-4 text-base font-black text-white shadow-lg shadow-indigo-100 transition hover:bg-indigo-700 active:scale-[0.99]"
                >
                  Check Answer
                </button>
              ) : null
            }
          />
        )}

        {/* Wrong */}
        {status === "wrong" && (
          <div className="mt-6">
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              ❌ Not correct. Try again. ({attempts}/{MAX_ATTEMPTS})
            </div>

            {!stickyCfg.show && (
              <button
                onClick={handleTryAgain}
                className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white hover:opacity-90"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {/* XP Toast */}
        {showXPToast && (
          <div
            className={[
              "fixed top-24 right-6 z-50 rounded-2xl px-5 py-3 text-white shadow-2xl shadow-purple-200",
              "font-black text-lg tracking-wide",
              "transition-all duration-300 ease-out",
              xpToastPhase === "enter" || xpToastPhase === "shown"
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 -translate-y-2 scale-95",
              "bg-gradient-to-r from-violet-600 to-purple-600",
            ].join(" ")}
          >
            +{earnedXP} XP ✨
          </div>
        )}

        {/* Reveal */}
        {status === "reveal" && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-900">
              📘 Good attempt! Here is the correct sentence:
            </div>

            {(() => {
              const text = String(expectedAnswer || englishFull || "").trim();
              const words = Array.isArray(text)
                ? text
                : String(text).trim().split(/\s+/).filter(Boolean);

              if (!words.length) {
                return (
                  <div className="mt-2 text-sm text-slate-600">
                    (No answer available for this item.)
                  </div>
                );
              }

              return (
                <div className="mt-3 flex flex-wrap gap-2">
                  {words.map((word, index) => (
                    <span
                      key={`${word}-${index}`}
                      className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-800"
                    >
                      {word}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
      <StickyCTABar cfg={stickyCfg} />

      {/* ✅ Lesson completion modal */}
      {/* 🛑 BANNER KILL SWITCH: Only show if questions are actually finished */}
      {isComplete &&
        currentIndex > 0 &&
        currentIndex >= (lessonExercises?.length || 1) && (
          <div className="fixed inset-0 z-[500] flex items-center justify-center bg-slate-900/90 backdrop-blur-xl p-4">
            <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
              {/* Top accent */}
              <div className="h-1.5 w-full bg-emerald-500" />

              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Lesson complete
                    </div>
                    <div className="mt-1 text-2xl font-extrabold text-slate-900">
                      🎉 Great job!
                    </div>
                    <div className="mt-2 text-sm text-slate-600">
                      You earned{" "}
                      <span className="font-bold text-slate-900">
                        +{completionXp}
                      </span>{" "}
                      XP bonus.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowCompleteModal(false)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>

                <div className="mt-5 grid gap-3">
                  {/* Primary */}
                  <button
                    className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                    onClick={() => {
                      setShowCompleteModal(false);
                      const next = Number(lid) + 1;
                      navigate(
                        `${base}/lesson/${next}?difficulty=${encodeURIComponent(difficulty)}`,
                        { replace: true },
                      );
                    }}
                  >
                    Continue to next lesson →
                  </button>

                  {/* Secondary row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                      onClick={() => {
                        setShowCompleteModal(false);
                        initQuiz();
                      }}
                    >
                      Repeat lesson
                    </button>

                    <button
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                      onClick={() => {
                        setShowCompleteModal(false);
                        navigate("/leaderboard");
                      }}
                    >
                      View leaderboard
                    </button>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-600">
                    Keep your streak going
                  </div>
                  <div className="mt-1 text-sm text-slate-700">
                    Practice 10 minutes daily for faster automatic sentence
                    formation.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      {/* ✅ Correct banner (Typing + Reorder) — bottom floating */}
      {status === "correct" && Number(earnedXP || 0) > 0 && (
        <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-50">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-5 py-3 text-center shadow-lg">
            <div className="font-semibold text-emerald-800">Correct ✅</div>
            <div className="text-emerald-900 font-extrabold">
              +{Number(earnedXP || 0)} XP
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// DISK_MARKER_1770108265
